import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    DownOutlined,
    DownloadOutlined,
    EditOutlined,
    HistoryOutlined,
    SyncOutlined,
    UpOutlined,
    UploadOutlined
} from '@ant-design/icons';
import {
    Button,
    Checkbox,
    ConfigProvider,
    DatePicker,
    Dropdown,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    TimePicker,
    Tooltip,
    Upload
} from 'antd';
import { useBackdropClose } from '../hooks/useBackdropClose';
import ruRU from 'antd/locale/ru_RU';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/ru';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import BalanceOperationsLogs from '../components/balance-operations-logs/balance-operations-logs';
import CustomPagination from '../components/custom-pagination/CustomPagination';
import CustomSelect from '../components/custom-select/CustomSelect';
import IntegrationCheckResult from '../components/integration-check-result/IntegrationCheckResult';
import FullImportProgressModal, {
  applyFullImportUploadProgress,
  advanceFullImportServerPhases,
  buildFullRestoreStorageRestorePhases,
  buildFullRestoreUploadRestorePhases,
  buildProxyBackupImportPhases,
  completeFullImportProgressState,
  createFullImportProgressState,
  FULL_IMPORT_VALIDATE_PHASES,
  tickFullImportWaitingProgress,
} from '../components/full-import-progress/full-import-progress';
import { getSettingsSection } from '../config/settings-routes';
import { apiService, formatFullBackupArchiveError, parseApiErrorDetail } from '../services/api';
import { MAX_TELEGRAM_BOT_PROXIES, normalizeTelegramBotProxies } from '../utils/cabinetMarketingUtils';
import { formatMskDateTime, parseMskInstant } from '../utils/moscow-calendar-date';
import ProxyChecker from './ProxyChecker';
import '../styles/settings.css';

dayjs.extend(customParseFormat);
dayjs.locale('ru');

const { Option } = Select;

const lookupTelegramBotProxyHealth = (healthMap, proxyUrl) => {
  const trimmed = String(proxyUrl || '').trim();
  if (!trimmed) return null;
  const map = healthMap && typeof healthMap === 'object' ? healthMap : {};
  if (map[trimmed] && typeof map[trimmed] === 'object') return map[trimmed];
  const lower = trimmed.toLowerCase();
  if (map[lower] && typeof map[lower] === 'object') return map[lower];
  const found = Object.entries(map).find(([key]) => String(key).toLowerCase() === lower);
  return found && found[1] && typeof found[1] === 'object' ? found[1] : null;
};

/* ---------- SVG icon components ---------- */
const RefreshIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_sett_refresh)">
      <path d="M6.0001 0.999894C6.66336 1.00207 7.31959 1.13576 7.93084 1.39324C8.54208 1.65071 9.09619 2.02685 9.5611 2.49989H8.0001C7.86749 2.49989 7.74032 2.55257 7.64655 2.64634C7.55278 2.74011 7.5001 2.86729 7.5001 2.99989C7.5001 3.1325 7.55278 3.25968 7.64655 3.35345C7.74032 3.44722 7.86749 3.49989 8.0001 3.49989H10.0716C10.3178 3.49976 10.5539 3.4019 10.728 3.2278C10.9021 3.0537 11 2.81761 11.0001 2.57139V0.499894C11.0001 0.367286 10.9474 0.240109 10.8537 0.14634C10.7599 0.0525723 10.6327 -0.000106158 10.5001 -0.000106158C10.3675 -0.000106158 10.2403 0.0525723 10.1465 0.14634C10.0528 0.240109 10.0001 0.367286 10.0001 0.499894V1.53889C9.17369 0.79462 8.15639 0.295099 7.06215 0.0962684C5.96791 -0.102562 4.83991 0.00714982 3.80449 0.413118C2.76907 0.819085 1.86711 1.50528 1.19961 2.39486C0.532105 3.28443 0.125422 4.34227 0.0251013 5.44989C0.0186429 5.51952 0.0267591 5.58973 0.0489327 5.65605C0.0711063 5.72237 0.106851 5.78334 0.153889 5.83509C0.200928 5.88683 0.258228 5.92821 0.32214 5.95658C0.386052 5.98496 0.455174 5.99971 0.525101 5.99989C0.647395 6.00145 0.765868 5.95731 0.857331 5.87612C0.948793 5.79492 1.00666 5.68251 1.0196 5.56089C1.13091 4.31627 1.70359 3.15825 2.62514 2.31431C3.54669 1.47038 4.75051 1.00154 6.0001 0.999894Z" fill="currentColor"/>
      <path d="M11.4756 6.00004C11.3533 5.99848 11.2349 6.04262 11.1434 6.12382C11.0519 6.20501 10.9941 6.31742 10.9811 6.43904C10.8985 7.39066 10.5446 8.2986 9.96145 9.05515C9.37831 9.8117 8.5904 10.3851 7.6912 10.7073C6.79199 11.0296 5.81922 11.0871 4.8883 10.8731C3.95738 10.6591 3.10737 10.1826 2.43912 9.50005H4.00012C4.13273 9.50005 4.25991 9.44737 4.35368 9.3536C4.44744 9.25983 4.50012 9.13266 4.50012 9.00005C4.50012 8.86744 4.44744 8.74026 4.35368 8.64649C4.25991 8.55272 4.13273 8.50005 4.00012 8.50005H1.92862C1.80667 8.49998 1.6859 8.52395 1.57322 8.57059C1.46054 8.61723 1.35816 8.68562 1.27193 8.77185C1.18569 8.85808 1.1173 8.96047 1.07067 9.07315C1.02403 9.18583 1.00006 9.3066 1.00012 9.42855V11.5001C1.00012 11.6327 1.0528 11.7598 1.14657 11.8536C1.24034 11.9474 1.36751 12.0001 1.50012 12.0001C1.63273 12.0001 1.75991 11.9474 1.85368 11.8536C1.94744 11.7598 2.00012 11.6327 2.00012 11.5001V10.4611C2.82654 11.2053 3.84383 11.7048 4.93807 11.9037C6.03232 12.1025 7.16031 11.9928 8.19573 11.5868C9.23115 11.1809 10.1331 10.4947 10.8006 9.60509C11.4681 8.71551 11.8748 7.65767 11.9751 6.55004C11.9816 6.48041 11.9735 6.4102 11.9513 6.34388C11.9291 6.27756 11.8934 6.21659 11.8463 6.16485C11.7993 6.11311 11.742 6.07173 11.6781 6.04335C11.6142 6.01498 11.545 6.00023 11.4751 6.00004H11.4756Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="clip0_sett_refresh">
        <rect width="12" height="12" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 5.5H6.5V0.5C6.5 0.367 6.447 0.24 6.354 0.146C6.26 0.053 6.133 0 6 0C5.867 0 5.74 0.053 5.646 0.146C5.553 0.24 5.5 0.367 5.5 0.5V5.5H0.5C0.367 5.5 0.24 5.553 0.146 5.646C0.053 5.74 0 5.867 0 6C0 6.133 0.053 6.26 0.146 6.354C0.24 6.447 0.367 6.5 0.5 6.5H5.5V11.5C5.5 11.633 5.553 11.76 5.646 11.854C5.74 11.947 5.867 12 6 12C6.133 12 6.26 11.947 6.354 11.854C6.447 11.76 6.5 11.633 6.5 11.5V6.5H11.5C11.633 6.5 11.76 6.447 11.854 6.354C11.947 6.26 12 6.133 12 6C12 5.867 11.947 5.74 11.854 5.646C11.76 5.553 11.633 5.5 11.5 5.5Z" fill="currentColor"/>
  </svg>
);

const CATEGORY_LABELS = {
  proxy: 'Прокси',
  modem: 'Модем',
  mismatch: 'Рассинхрон',
  user: 'Пользователь',
  connection: 'Подключение',
  backup: 'Бэкап',
  system: 'Система',
  sync: 'Синхронизация',
  autoreplace: 'Автозамена',
};

const LEVEL_LABELS = { info: 'Info', warning: 'Warning', error: 'Error' };

const LK_SECTION_DIRTY_INITIAL = Object.freeze({
  balance: false,
  privilege: false,
  scripts: false,
  autoDelete: false,
  support: false,
  fingerprints: false,
  tgProxies: false,
  forbiddenResources: false,
});

const CLIENT_CABINET_TABS = [
  { key: 'main', label: 'Основное' },
  { key: 'scripts', label: 'Скрипты' },
  { key: 'tg-proxies', label: 'Прокси TG' },
  { key: 'forbidden-resources', label: 'Запрещенные ресурсы' },
];

/** Парсинг ID списков Kraken / портов из строки «1, 2, 5» (как в ProxyCreatePanel). */
const parseKrakenIntListInput = (raw) => {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n) && n > 0);
  }
  return String(raw)
    .trim()
    .split(/[,;\s]+/)
    .filter(Boolean)
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
};

const formatKrakenIntListInput = (arr) => (
  Array.isArray(arr) ? arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0).join(', ') : ''
);

const LOGS_SECTION_TABS = [
  { key: 'logs', label: 'Логи системы' },
  { key: 'proxy-replacement-logs', label: 'Логи автозамены' },
  { key: 'modem-reboot-logs', label: 'Логи перезагрузки' },
  { key: 'balance-operations-logs', label: 'Логи по операциям' },
];

const isLogsSectionTab = (tabKey) => LOGS_SECTION_TABS.some((tab) => tab.key === tabKey);

const INTEGRATIONS_SECRET_MASK = '••••••••';

const resolveUnpaidProxyDeleteGraceHours = (settings) => {
  const safe = settings && typeof settings === 'object' ? settings : {};
  if (safe.unpaid_proxy_delete_grace_hours != null && safe.unpaid_proxy_delete_grace_hours !== '') {
    return Number(safe.unpaid_proxy_delete_grace_hours);
  }
  if (safe.unpaid_proxy_delete_grace_days != null && safe.unpaid_proxy_delete_grace_days !== '') {
    return Number(safe.unpaid_proxy_delete_grace_days) * 24;
  }
  return 168;
};

/**
 * Время запуска: допускаются 23:15, 23.15, 23,15 и т.п. → нормализация в ЧЧ:ММ для API.
 */
const parseRunTimeHhmm = (raw) => {
  if (raw == null) return null;
  const normalized = String(raw)
    .trim()
    .replace(/\./g, ':')
    .replace(/,/g, ':')
    .replace(/\s+/g, '');
  if (!normalized) return null;
  const parts = normalized.split(':').filter((p) => p !== '');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const Settings = () => {
  const location = useLocation();
  const settingsSection = getSettingsSection(location.pathname);
  const isClientCabinetSection = settingsSection === 'client-cabinet';
  const isLogsSection = settingsSection === 'logs';
  const isBackupsSection = settingsSection === 'backups';
  const settingsPageTitle = isClientCabinetSection
    ? 'Кабинет клиента'
    : isLogsSection
      ? 'Логи'
      : isBackupsSection
        ? 'Бэкапы'
        : 'Proxy Manager';
  const [clientCabinetTab, setClientCabinetTab] = useState('main');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  
  // Резервное копирование
  const [krakenConnections, setKrakenConnections] = useState([]);
  const [selectedConnections, setSelectedConnections] = useState([]);
  const [allServers, setAllServers] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [simBackupLoading, setSimBackupLoading] = useState(false);
  const [autoBackups, setAutoBackups] = useState([]);
  const [autoBackupPage, setAutoBackupPage] = useState(1);
  const [autoBackupPerPage, setAutoBackupPerPage] = useState(10);
  const [fullBackupPage, setFullBackupPage] = useState(1);
  const [fullBackupPerPage, setFullBackupPerPage] = useState(10);
  const [backupForm] = Form.useForm();

  // Полный бэкап системы
  const [fullBackups, setFullBackups] = useState([]);
  const [fullBackupLoading, setFullBackupLoading] = useState(false);
  const [fullImportFile, setFullImportFile] = useState(null);
  const [fullImportLoading, setFullImportLoading] = useState(false);
  const [fullImportRestoreSystemConfigs, setFullImportRestoreSystemConfigs] = useState(false);
  const [fullImportProgress, setFullImportProgress] = useState(null);
  const fullImportSimTimerRef = useRef(null);
  const [proxyImportProgress, setProxyImportProgress] = useState(null);
  const proxyImportSimTimerRef = useRef(null);

  useEffect(() => () => {
    if (fullImportSimTimerRef.current) {
      clearInterval(fullImportSimTimerRef.current);
    }
    if (proxyImportSimTimerRef.current) {
      clearInterval(proxyImportSimTimerRef.current);
    }
  }, []);

  const [sshModalVisible, setSshModalVisible] = useState(false);
  const [sshTransferFile, setSshTransferFile] = useState('');
  const [sshTransferLoading, setSshTransferLoading] = useState(false);
  const [sshForm] = Form.useForm();

  // SSH-сервер для бэкапов
  const [sshConfig, setSshConfig] = useState(null);
  const [sshConfigModalVisible, setSshConfigModalVisible] = useState(false);
  const [sshConfigLoading, setSshConfigLoading] = useState(false);

  // Окна правки пользователя и SSH: затемнение закрывает только настоящим щелчком — нажатие и отпускание на нём
  // (владелец 29.10, DF85); крестик и Esc — тем же обработчиком
  const handleUserModalCancel = () => {
    setModalVisible(false);
    setEditingUser(null);
    form.resetFields();
  };
  const userModalBackdrop = useBackdropClose(handleUserModalCancel);
  const sshModalBackdrop = useBackdropClose(() => setSshModalVisible(false));
  const sshConfigModalBackdrop = useBackdropClose(() => setSshConfigModalVisible(false));
  const [sshConfigForm] = Form.useForm();
  const [sshTestLoading, setSshTestLoading] = useState(false);
  const [sshKeyGenLoading, setSshKeyGenLoading] = useState(false);
  const [sshKeyInstallLoading, setSshKeyInstallLoading] = useState(false);

  // Настройки модемов по умолчанию
  const [modemDefaultsForm] = Form.useForm();
  const [modemDefaultsLoading, setModemDefaultsLoading] = useState(false);
  const [modemResyncForm] = Form.useForm();
  const [modemResyncLoading, setModemResyncLoading] = useState(false);
  const [modemResyncRunLoading, setModemResyncRunLoading] = useState(false);
  const [modemResyncMeta, setModemResyncMeta] = useState({
    last_run_at: null,
    last_run_summary: null,
  });
  const [krakenCleanupForm] = Form.useForm();
  const [krakenCleanupLoading, setKrakenCleanupLoading] = useState(false);
  const [krakenCleanupRunLoading, setKrakenCleanupRunLoading] = useState(false);
  const [krakenCleanupMeta, setKrakenCleanupMeta] = useState({
    last_run_at: null,
    last_run_summary: null,
  });
  const [osfpList, setOsfpList] = useState([]);
  const [operatorTemplates, setOperatorTemplates] = useState([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [integrationsSettings, setIntegrationsSettings] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationsSavingSectionId, setIntegrationsSavingSectionId] = useState(null);
  const [integrationsCheckingSectionId, setIntegrationsCheckingSectionId] = useState(null);
  const [integrationsChecks, setIntegrationsChecks] = useState({});
  const [operatorsPreviewInput, setOperatorsPreviewInput] = useState('');
  const [operatorsPreviewResult, setOperatorsPreviewResult] = useState([]);

  // Настройки личного кабинета клиента (баланс, привилегии, сторонние скрипты, разное)
  const [cabinetDefaultsForm] = Form.useForm();
  const [integrationsForm] = Form.useForm();
  const [cabinetDefaultsLoading, setCabinetDefaultsLoading] = useState(false);
  const [lkSectionDirty, setLkSectionDirty] = useState(() => ({ ...LK_SECTION_DIRTY_INITIAL }));
  const [lkSectionSaving, setLkSectionSaving] = useState(() => ({ ...LK_SECTION_DIRTY_INITIAL }));
  const lkSectionDirtyRef = useRef({ ...LK_SECTION_DIRTY_INITIAL });
  const cabinetLkFormFieldsRef = useRef({
    default_balance_rub: 150,
    default_privilege_tier: 'gold',
    default_privilege_duration_days: 30,
    support_telegram_url: 'https://t.me/+79939885347',
    support_max_url: 'https://max.ru/u/f9LHodD0cOJXLd0Kb1QPewMTs5S1ZtcXILIC5HR-Ciy0D8K9OPFYuWSIUpg',
  });
  const loadCabinetDefaultsSeqRef = useRef(0);
  /** Поля unavailable_* убраны из формы; для полного payload берём последние значения с сервера. */
  const cabinetMiscUnavailableRef = useRef({
    unavailable_proxy_auto_delete_enabled: false,
    unavailable_proxy_delete_after_days: 14,
  });
  const cabinetTelegramBotProxiesRef = useRef([]);
  const [cabinetTelegramBotProxies, setCabinetTelegramBotProxies] = useState([]);
  const [cabinetTelegramBotProxyHealth, setCabinetTelegramBotProxyHealth] = useState({});
  const [cabinetTelegramBotProxyHealthChecking, setCabinetTelegramBotProxyHealthChecking] = useState(false);
  const telegramBotProxyHealthReqRef = useRef(0);
  const telegramBotProxyHealthCheckingRef = useRef(false);
  const [cabinetPofCatalog, setCabinetPofCatalog] = useState([]);
  const [cabinetPofCatalogLoading, setCabinetPofCatalogLoading] = useState(false);
  const loadCabinetPofCatalogSeqRef = useRef(0);
  const cabinetLkApplyingRef = useRef(false);
  const [cabinetScripts, setCabinetScripts] = useState([]);
  /** Раскрытие блока «код и таргетинг» для строки стороннего скрипта (ключ — id или стабильный индекс). */
  const [cabinetScriptBodyExpanded, setCabinetScriptBodyExpanded] = useState({});
  const cabinetScriptsRef = useRef([]);

  // Системные логи
  const [systemLogs, setSystemLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsFilters, setLogsFilters] = useState({
    category: '',
    level: '',
    search: '',
    date_from: '',
    date_to: '',
    log_client: '',
    log_server: '',
    log_modem: '',
    log_port: '',
  });
  const [balanceOpsRefreshNonce, setBalanceOpsRefreshNonce] = useState(0);

  const fetchKrakenConnections = async () => {
    try {
      const response = await apiService.getKrakenConnections();
      // Убеждаемся, что это массив
      const connectionsSource = response?.data?.connections || response?.data;
      const connections = Array.isArray(connectionsSource) ? connectionsSource : [];
      setKrakenConnections(connections);
      return connections;
    } catch (error) {
      // Устанавливаем пустой массив при ошибке, чтобы не ломать UI
      setKrakenConnections([]);
      return [];
    }
  };
  
  const fetchAutoBackups = async () => {
    try {
      const response = await apiService.listAutoBackups();
      // Убеждаемся, что это массив
      const backups = Array.isArray(response?.data?.backups) ? response.data.backups : [];
      setAutoBackups(backups);
      setAutoBackupPage(1);
    } catch (error) {
      // Устанавливаем пустой массив при ошибке, чтобы не ломать UI
      setAutoBackups([]);
    }
  };

  const handleLoadOsfpList = async (connections = []) => {
    try {
      const activeConnection = connections.find(conn => conn && conn.is_active) || connections[0];
      if (!activeConnection) {
        setOsfpList([]);
        return;
      }
      const response = await apiService.getKrakenOsfpList(activeConnection.id);
      let list = [];
      if (response && response.data) {
        if (Array.isArray(response.data.osfp_list)) {
          list = response.data.osfp_list;
        } else if (Array.isArray(response.data.data)) {
          list = response.data.data;
        } else if (Array.isArray(response.data)) {
          list = response.data;
        }
      } else if (Array.isArray(response)) {
        list = response;
      }
      setOsfpList(list);
    } catch (error) {
      setOsfpList([]);
    }
  };

  const handleLoadModemDefaults = async () => {
    try {
      setModemDefaultsLoading(true);
      const response = await apiService.getModemDefaults();
      const defaults = response?.data?.data || response?.data || {};
      modemDefaultsForm.setFieldsValue({
        monitoring_interval: defaults.monitoring_interval,
        reconnect_type: defaults.reconnect_type,
        reconnect_interval: defaults.reconnect_interval,
        reconnect_min: defaults.reconnect_min,
        mtu: defaults.mtu,
        is_osfp: defaults.is_osfp,
        osfp_id: defaults.osfp_id
      });
    } catch (error) {
      message.error('Ошибка загрузки настроек модемов по умолчанию');
    } finally {
      setModemDefaultsLoading(false);
    }
  };

  const handleLoadModemResync = async () => {
    const applyResyncPayload = (payload) => {
      const d = payload && typeof payload === 'object' ? payload : {};
      modemResyncForm.setFieldsValue({
        enabled: !!d.enabled,
        run_time_hhmm: d.run_time_hhmm || '03:00',
      });
      setModemResyncMeta({
        last_run_at: d.last_run_at || null,
        last_run_summary: d.last_run_summary || null,
      });
    };

    try {
      setModemResyncLoading(true);
      const response = await apiService.getModemSettingsResync();
      const root = response?.data;
      const payload =
        root && typeof root === 'object' && Object.prototype.hasOwnProperty.call(root, 'data')
          ? root.data
          : root;
      applyResyncPayload(payload);
    } catch (error) {
      applyResyncPayload({
        enabled: false,
        run_time_hhmm: '03:00',
        last_run_at: null,
        last_run_summary: null,
      });
    } finally {
      setModemResyncLoading(false);
    }
  };

  const handleModemResyncTimeBlur = () => {
    const v = modemResyncForm.getFieldValue('run_time_hhmm');
    const n = parseRunTimeHhmm(v);
    if (n) {
      modemResyncForm.setFieldsValue({ run_time_hhmm: n });
    }
  };

  const handleSaveModemResync = async (values) => {
    try {
      setModemResyncLoading(true);
      const normalizedTime = parseRunTimeHhmm(values.run_time_hhmm);
      if (!normalizedTime) {
        message.error('Время: укажите в формате ЧЧ:ММ или ЧЧ.ММ (например 23:15 или 23.15)');
        return;
      }
      await apiService.setModemSettingsResync({
        enabled: values.enabled === true,
        run_time_hhmm: normalizedTime,
      });
      message.success('Сохранено');
      await handleLoadModemResync();
    } catch (error) {
      message.error('Ошибка сохранения');
    } finally {
      setModemResyncLoading(false);
    }
  };

  const handleLoadKrakenCleanup = async () => {
    const applyPayload = (payload) => {
      const d = payload && typeof payload === 'object' ? payload : {};
      krakenCleanupForm.setFieldsValue({
        enabled: !!d.enabled,
        interval_minutes: d.interval_minutes ?? 30,
        max_attempts: d.max_attempts ?? 10,
      });
      setKrakenCleanupMeta({
        last_run_at: d.last_run_at || null,
        last_run_summary: d.last_run_summary || null,
      });
    };

    try {
      setKrakenCleanupLoading(true);
      const response = await apiService.getKrakenProxyCleanup();
      const root = response?.data;
      const payload =
        root && typeof root === 'object' && Object.prototype.hasOwnProperty.call(root, 'data')
          ? root.data
          : root;
      applyPayload(payload);
    } catch (error) {
      applyPayload({
        enabled: false,
        interval_minutes: 30,
        max_attempts: 10,
        last_run_at: null,
        last_run_summary: null,
      });
    } finally {
      setKrakenCleanupLoading(false);
    }
  };

  const handleSaveKrakenCleanup = async (values) => {
    try {
      setKrakenCleanupLoading(true);
      const intervalMinutes = Number(values.interval_minutes);
      if (!Number.isFinite(intervalMinutes) || intervalMinutes < 1 || intervalMinutes > 1440) {
        message.error('Интервал: укажите число минут от 1 до 1440');
        return;
      }
      const maxAttempts = Number(values.max_attempts);
      await apiService.setKrakenProxyCleanup({
        enabled: values.enabled === true,
        interval_minutes: intervalMinutes,
        max_attempts: Number.isFinite(maxAttempts) && maxAttempts > 0 ? maxAttempts : 10,
      });
      message.success('Сохранено');
      await handleLoadKrakenCleanup();
    } catch (error) {
      message.error('Ошибка сохранения');
    } finally {
      setKrakenCleanupLoading(false);
    }
  };

  const handleRunKrakenCleanupNow = async () => {
    try {
      setKrakenCleanupRunLoading(true);
      const response = await apiService.postKrakenProxyCleanupRunNow();
      const summary = response?.data?.data || {};
      message.success(
        `Очистка: обработано ${summary.processed ?? 0}, done ${summary.done ?? 0}, ошибок ${summary.failed ?? 0}`
        + (summary.reconciled ? `, leftover ${summary.reconciled}` : '')
      );
      await handleLoadKrakenCleanup();
    } catch (error) {
      message.error('Ошибка запуска очистки');
    } finally {
      setKrakenCleanupRunLoading(false);
    }
  };

  const handleRunModemResyncNow = async () => {
    try {
      setModemResyncRunLoading(true);
      const response = await apiService.postModemSettingsResyncRunNow();
      const summary = response?.data?.data || {};
      const skip = summary.skipped_no_connection;
      message.success(
        `Готово: модемов ${summary.processed ?? 0}, успешно в Kraken ${summary.applied_kraken ?? 0}, ошибок ${summary.failed ?? 0}` +
          (skip ? `, пропущено без подключения: ${skip}` : '')
      );
      await handleLoadModemResync();
    } catch (error) {
      message.error('Ошибка запуска пересохранения');
    } finally {
      setModemResyncRunLoading(false);
    }
  };

  const handleSaveModemDefaults = async (values) => {
    try {
      setModemDefaultsLoading(true);
      const payload = {
        monitoring_interval: values.monitoring_interval,
        reconnect_type: values.reconnect_type,
        reconnect_interval: values.reconnect_interval,
        reconnect_min: values.reconnect_min,
        mtu: values.mtu,
        is_osfp: !!values.is_osfp,
        osfp_id: values.is_osfp ? (values.osfp_id || null) : null
      };
      const response = await apiService.setModemDefaults(payload);
      const saved = response?.data?.data || payload;
      modemDefaultsForm.setFieldsValue(saved);
      message.success('Настройки модемов сохранены');
    } catch (error) {
      message.error('Ошибка сохранения настроек модемов');
    } finally {
      setModemDefaultsLoading(false);
    }
  };

  const applyCabinetDefaultsResponse = useCallback((data) => {
    const safe = data && typeof data === 'object' ? data : {};
    cabinetLkApplyingRef.current = true;
    cabinetDefaultsForm.setFieldsValue({
      default_balance_rub: Number(safe.default_balance_rub ?? 150),
      default_privilege_tier: safe.default_privilege_tier ?? 'gold',
      default_privilege_duration_days: Number(safe.default_privilege_duration_days ?? 30),
      unpaid_proxy_auto_delete_enabled: safe.unpaid_proxy_auto_delete_enabled !== false,
      unpaid_proxy_delete_grace_hours: resolveUnpaidProxyDeleteGraceHours(safe),
      support_telegram_url: String(
        safe.support_telegram_url ?? 'https://t.me/+79939885347',
      ),
      support_max_url: String(
        safe.support_max_url ?? 'https://max.ru/u/f9LHodD0cOJXLd0Kb1QPewMTs5S1ZtcXILIC5HR-Ciy0D8K9OPFYuWSIUpg',
      ),
      cabinet_pof_visible_ids: Array.isArray(safe.cabinet_pof_visible_ids)
        ? safe.cabinet_pof_visible_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
        : [1, 2, 5, 6],
      default_kraken_is_allow: safe.default_kraken_is_allow === true,
      default_kraken_is_ports: safe.default_kraken_is_ports === true,
      default_kraken_allow_input: formatKrakenIntListInput(safe.default_kraken_allow),
      default_kraken_targetport_input: formatKrakenIntListInput(safe.default_kraken_targetport),
    });
    cabinetMiscUnavailableRef.current = {
      unavailable_proxy_auto_delete_enabled: safe.unavailable_proxy_auto_delete_enabled === true,
      unavailable_proxy_delete_after_days: Math.min(
        365,
        Math.max(1, Number(safe.unavailable_proxy_delete_after_days ?? 14)),
      ),
    };
    cabinetLkFormFieldsRef.current = {
      default_balance_rub: Number(safe.default_balance_rub ?? 150),
      default_privilege_tier: safe.default_privilege_tier ?? 'gold',
      default_privilege_duration_days: Number(safe.default_privilege_duration_days ?? 30),
      support_telegram_url: String(
        safe.support_telegram_url ?? 'https://t.me/+79939885347',
      ),
      support_max_url: String(
        safe.support_max_url ?? 'https://max.ru/u/f9LHodD0cOJXLd0Kb1QPewMTs5S1ZtcXILIC5HR-Ciy0D8K9OPFYuWSIUpg',
      ),
    };
    const scripts = Array.isArray(safe.scripts) ? safe.scripts : [];
    const nextScripts = scripts.map((item) => ({
      id: item.id,
      name: item.name || '',
      content: item.content || '',
      pages: Array.isArray(item.pages) && item.pages.length ? item.pages : ['all'],
      tiers: Array.isArray(item.tiers) && item.tiers.length ? item.tiers : ['all'],
      enabled: item.enabled !== false,
      starts_at: item.starts_at || null,
      ends_at: item.ends_at || null,
    }));
    cabinetScriptsRef.current = nextScripts;
    setCabinetScripts(nextScripts);
    const nextBotProxies = normalizeTelegramBotProxies(safe.telegram_bot_proxies);
    cabinetTelegramBotProxiesRef.current = nextBotProxies;
    setCabinetTelegramBotProxies(nextBotProxies);
    if (!telegramBotProxyHealthCheckingRef.current) {
      const nextHealth = safe.telegram_bot_proxy_health;
      setCabinetTelegramBotProxyHealth(nextHealth && typeof nextHealth === 'object' ? nextHealth : {});
    }
    setLkSectionDirty({ ...LK_SECTION_DIRTY_INITIAL });
    window.setTimeout(() => {
      cabinetLkApplyingRef.current = false;
    }, 0);
  }, [cabinetDefaultsForm]);

  const markLkSectionDirty = useCallback((section) => {
    setLkSectionDirty((prev) => ({ ...prev, [section]: true }));
  }, []);

  const applyCabinetDefaultsSliceFromSaved = useCallback((data, section) => {
    const safe = data && typeof data === 'object' ? data : {};
    cabinetLkApplyingRef.current = true;
    if (section === 'balance') {
      const v = Number(safe.default_balance_rub ?? 150);
      cabinetDefaultsForm.setFieldValue('default_balance_rub', v);
      cabinetLkFormFieldsRef.current = { ...cabinetLkFormFieldsRef.current, default_balance_rub: v };
      setLkSectionDirty((prev) => ({ ...prev, balance: false }));
    } else if (section === 'privilege') {
      const tier = safe.default_privilege_tier ?? 'gold';
      const days = Number(safe.default_privilege_duration_days ?? 30);
      cabinetDefaultsForm.setFieldsValue({
        default_privilege_tier: tier,
        default_privilege_duration_days: days,
      });
      cabinetLkFormFieldsRef.current = {
        ...cabinetLkFormFieldsRef.current,
        default_privilege_tier: tier,
        default_privilege_duration_days: days,
      };
      setLkSectionDirty((prev) => ({ ...prev, privilege: false }));
    } else if (section === 'scripts') {
      const scripts = Array.isArray(safe.scripts) ? safe.scripts : [];
      const nextScripts = scripts.map((item) => ({
        id: item.id,
        name: item.name || '',
        content: item.content || '',
        pages: Array.isArray(item.pages) && item.pages.length ? item.pages : ['all'],
        tiers: Array.isArray(item.tiers) && item.tiers.length ? item.tiers : ['all'],
        enabled: item.enabled !== false,
        starts_at: item.starts_at || null,
        ends_at: item.ends_at || null,
      }));
      cabinetScriptsRef.current = nextScripts;
      setCabinetScripts(nextScripts);
      setLkSectionDirty((prev) => ({ ...prev, scripts: false }));
    } else if (section === 'autoDelete') {
      cabinetDefaultsForm.setFieldsValue({
        unpaid_proxy_auto_delete_enabled: safe.unpaid_proxy_auto_delete_enabled !== false,
        unpaid_proxy_delete_grace_hours: resolveUnpaidProxyDeleteGraceHours(safe),
      });
      if (Object.prototype.hasOwnProperty.call(safe, 'unavailable_proxy_auto_delete_enabled')) {
        cabinetMiscUnavailableRef.current.unavailable_proxy_auto_delete_enabled =
          safe.unavailable_proxy_auto_delete_enabled === true;
      }
      if (Object.prototype.hasOwnProperty.call(safe, 'unavailable_proxy_delete_after_days')) {
        cabinetMiscUnavailableRef.current.unavailable_proxy_delete_after_days = Math.min(
          365,
          Math.max(1, Number(safe.unavailable_proxy_delete_after_days ?? 14)),
        );
      }
      setLkSectionDirty((prev) => ({ ...prev, autoDelete: false }));
    } else if (section === 'support') {
      const telegramUrl = String(
        safe.support_telegram_url ?? cabinetLkFormFieldsRef.current.support_telegram_url ?? 'https://t.me/+79939885347',
      );
      const maxUrl = String(
        safe.support_max_url
          ?? cabinetLkFormFieldsRef.current.support_max_url
          ?? 'https://max.ru/u/f9LHodD0cOJXLd0Kb1QPewMTs5S1ZtcXILIC5HR-Ciy0D8K9OPFYuWSIUpg',
      );
      cabinetDefaultsForm.setFieldsValue({
        support_telegram_url: telegramUrl,
        support_max_url: maxUrl,
      });
      cabinetLkFormFieldsRef.current = {
        ...cabinetLkFormFieldsRef.current,
        support_telegram_url: telegramUrl,
        support_max_url: maxUrl,
      };
      setLkSectionDirty((prev) => ({ ...prev, support: false }));
    } else if (section === 'fingerprints') {
      if (Array.isArray(safe.cabinet_pof_visible_ids)) {
        cabinetDefaultsForm.setFieldValue(
          'cabinet_pof_visible_ids',
          safe.cabinet_pof_visible_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0),
        );
      }
      setLkSectionDirty((prev) => ({ ...prev, fingerprints: false }));
    } else if (section === 'tgProxies') {
      if (Object.prototype.hasOwnProperty.call(safe, 'telegram_bot_proxies')) {
        const nextBotProxies = normalizeTelegramBotProxies(safe.telegram_bot_proxies);
        cabinetTelegramBotProxiesRef.current = nextBotProxies;
        setCabinetTelegramBotProxies(nextBotProxies);
      }
      if (Object.prototype.hasOwnProperty.call(safe, 'telegram_bot_proxy_health')) {
        const nextHealth = safe.telegram_bot_proxy_health;
        setCabinetTelegramBotProxyHealth(nextHealth && typeof nextHealth === 'object' ? nextHealth : {});
      }
      setLkSectionDirty((prev) => ({ ...prev, tgProxies: false }));
    } else if (section === 'forbiddenResources') {
      cabinetDefaultsForm.setFieldsValue({
        default_kraken_is_allow: safe.default_kraken_is_allow === true,
        default_kraken_is_ports: safe.default_kraken_is_ports === true,
        default_kraken_allow_input: formatKrakenIntListInput(safe.default_kraken_allow),
        default_kraken_targetport_input: formatKrakenIntListInput(safe.default_kraken_targetport),
      });
      setLkSectionDirty((prev) => ({ ...prev, forbiddenResources: false }));
    }
    window.setTimeout(() => {
      cabinetLkApplyingRef.current = false;
    }, 0);
  }, [cabinetDefaultsForm]);

  const refreshTelegramBotProxyHealth = useCallback(async (urls) => {
    const toCheck = normalizeTelegramBotProxies(urls ?? cabinetTelegramBotProxiesRef.current);
    if (!toCheck.length) {
      telegramBotProxyHealthCheckingRef.current = false;
      setCabinetTelegramBotProxyHealth({});
      setCabinetTelegramBotProxyHealthChecking(false);
      return;
    }
    const reqId = telegramBotProxyHealthReqRef.current + 1;
    telegramBotProxyHealthReqRef.current = reqId;
    telegramBotProxyHealthCheckingRef.current = true;
    setCabinetTelegramBotProxyHealthChecking(true);
    try {
      const response = await apiService.checkTelegramBotProxiesHealth({ urls: toCheck });
      if (reqId !== telegramBotProxyHealthReqRef.current) return;
      const map = response?.data?.data?.health;
      setCabinetTelegramBotProxyHealth(map && typeof map === 'object' ? map : {});
    } catch (_error) {
      if (reqId !== telegramBotProxyHealthReqRef.current) return;
    } finally {
      if (reqId === telegramBotProxyHealthReqRef.current) {
        telegramBotProxyHealthCheckingRef.current = false;
        setCabinetTelegramBotProxyHealthChecking(false);
      }
    }
  }, []);

  useEffect(() => {
    lkSectionDirtyRef.current = lkSectionDirty;
  }, [lkSectionDirty]);

  useLayoutEffect(() => {
    cabinetScriptsRef.current = cabinetScripts;
  }, [cabinetScripts]);

  const mapCabinetScriptsForApi = useCallback(() => {
    const scr = cabinetScriptsRef.current;
    return scr.map((item) => ({
      id: item.id,
      name: item.name || '',
      content: item.content || '',
      pages: Array.isArray(item.pages) && item.pages.length ? item.pages : ['all'],
      tiers: Array.isArray(item.tiers) && item.tiers.length ? item.tiers : ['all'],
      enabled: item.enabled !== false,
      starts_at: item.starts_at || null,
      ends_at: item.ends_at || null,
    }));
  }, []);

  const handleSaveLkCabinetSection = useCallback(
    async (section) => {
      try {
        setLkSectionSaving((s) => ({ ...s, [section]: true }));
        let payload = {};
        if (section === 'balance') {
          await cabinetDefaultsForm.validateFields(['default_balance_rub']);
          const v = cabinetDefaultsForm.getFieldValue('default_balance_rub');
          payload = { default_balance_rub: Number(v ?? 0) };
        } else if (section === 'privilege') {
          await cabinetDefaultsForm.validateFields(['default_privilege_tier', 'default_privilege_duration_days']);
          const all = cabinetDefaultsForm.getFieldsValue([
            'default_privilege_tier',
            'default_privilege_duration_days',
          ]);
          payload = {
            default_privilege_tier: all.default_privilege_tier || 'gold',
            default_privilege_duration_days: Number(all.default_privilege_duration_days ?? 0),
          };
        } else if (section === 'scripts') {
          const scriptsList = mapCabinetScriptsForApi();
          const hasEmptyCode = scriptsList.some((s) => !String(s?.content ?? '').trim());
          if (hasEmptyCode) {
            message.error(
              'У одного из скриптов нет кода: нажмите стрелку «Развернуть» у строки, вставьте один тег <script …></script> (как в подсказке), затем сохраните.',
            );
            return;
          }
          payload = { scripts: scriptsList };
        } else if (section === 'autoDelete') {
          await cabinetDefaultsForm.validateFields(['unpaid_proxy_delete_grace_hours']);
          const autoDelete = cabinetDefaultsForm.getFieldsValue([
            'unpaid_proxy_auto_delete_enabled',
            'unpaid_proxy_delete_grace_hours',
          ]);
          payload = {
            unpaid_proxy_auto_delete_enabled: autoDelete.unpaid_proxy_auto_delete_enabled !== false,
            unpaid_proxy_delete_grace_hours: Math.min(
              8760,
              Math.max(0, Number(autoDelete.unpaid_proxy_delete_grace_hours ?? 168)),
            ),
          };
        } else if (section === 'support') {
          await cabinetDefaultsForm.validateFields(['support_telegram_url', 'support_max_url']);
          const support = cabinetDefaultsForm.getFieldsValue([
            'support_telegram_url',
            'support_max_url',
          ]);
          payload = {
            support_telegram_url: String(support.support_telegram_url ?? '').trim(),
            support_max_url: String(support.support_max_url ?? '').trim(),
          };
        } else if (section === 'fingerprints') {
          await cabinetDefaultsForm.validateFields(['cabinet_pof_visible_ids']);
          const fingerprints = cabinetDefaultsForm.getFieldsValue(['cabinet_pof_visible_ids']);
          const visiblePofIds = Array.isArray(fingerprints.cabinet_pof_visible_ids)
            ? fingerprints.cabinet_pof_visible_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
            : [];
          if (!visiblePofIds.length) {
            message.error('Выберите хотя бы один отпечаток ОС для личного кабинета');
            return;
          }
          payload = { cabinet_pof_visible_ids: visiblePofIds };
        } else if (section === 'tgProxies') {
          payload = {
            telegram_bot_proxies: normalizeTelegramBotProxies(cabinetTelegramBotProxiesRef.current),
          };
        } else if (section === 'forbiddenResources') {
          const forbidden = cabinetDefaultsForm.getFieldsValue([
            'default_kraken_is_allow',
            'default_kraken_is_ports',
            'default_kraken_allow_input',
            'default_kraken_targetport_input',
          ]);
          payload = {
            default_kraken_is_allow: forbidden.default_kraken_is_allow === true,
            default_kraken_is_ports: forbidden.default_kraken_is_ports === true,
            default_kraken_allow: parseKrakenIntListInput(forbidden.default_kraken_allow_input),
            default_kraken_targetport: parseKrakenIntListInput(forbidden.default_kraken_targetport_input),
          };
        }
        const response = await apiService.setCabinetDefaults(payload);
        const saved = response?.data?.data ?? payload;
        applyCabinetDefaultsSliceFromSaved(saved, section);
        if (section === 'scripts') {
          const sentN = Array.isArray(payload.scripts) ? payload.scripts.length : 0;
          const savedN = Array.isArray(saved?.scripts) ? saved.scripts.length : 0;
          if (sentN > savedN) {
            message.warning(
              'Часть скриптов не сохранена: допустим один тег <script src="//…" async></script> или <script>…</script> без лишнего текста вокруг; src только с http(s):// или // или от корня /.',
            );
          }
        }
        message.success(
          section === 'balance'
            ? 'Стартовый баланс сохранён'
            : section === 'privilege'
              ? 'Настройки привилегий сохранены'
              : section === 'autoDelete'
                  ? 'Автоудаление сохранено'
                  : section === 'support'
                    ? 'Мессенджеры поддержки сохранены'
                    : section === 'fingerprints'
                      ? 'Отпечатки сохранены'
                  : section === 'tgProxies'
                    ? 'Прокси Telegram-бота сохранены'
                    : section === 'scripts'
                      ? 'Скрипты сохранены'
                      : section === 'forbiddenResources'
                        ? 'Запрещенные ресурсы сохранены'
                        : 'Сохранено',
        );
        if (section === 'tgProxies') {
          refreshTelegramBotProxyHealth(normalizeTelegramBotProxies(cabinetTelegramBotProxiesRef.current));
        }
      } catch (error) {
        if (error?.errorFields?.length) {
          message.error('Проверьте заполнение полей');
        } else {
          message.error('Не удалось сохранить');
        }
      } finally {
        setLkSectionSaving((s) => ({ ...s, [section]: false }));
      }
    },
    [applyCabinetDefaultsSliceFromSaved, mapCabinetScriptsForApi, cabinetDefaultsForm, refreshTelegramBotProxyHealth],
  );

  const handleLoadCabinetDefaults = useCallback(async (opts) => {
    const force = opts && opts.force === true;
    const seq = ++loadCabinetDefaultsSeqRef.current;
    try {
      setCabinetDefaultsLoading(true);
      const response = await apiService.getCabinetDefaults();
      if (seq !== loadCabinetDefaultsSeqRef.current) return;
      if (!force && Object.values(lkSectionDirtyRef.current).some(Boolean)) return;
      applyCabinetDefaultsResponse(response?.data?.data || response?.data || {});
    } catch (error) {
      if (seq !== loadCabinetDefaultsSeqRef.current) return;
      message.error('Ошибка загрузки настроек ЛК');
    } finally {
      if (seq === loadCabinetDefaultsSeqRef.current) {
        setCabinetDefaultsLoading(false);
      }
    }
  }, [applyCabinetDefaultsResponse]);

  const handleLoadCabinetPofCatalog = useCallback(async () => {
    const seq = ++loadCabinetPofCatalogSeqRef.current;
    try {
      setCabinetPofCatalogLoading(true);
      const response = await apiService.getCabinetPofCatalog();
      if (seq !== loadCabinetPofCatalogSeqRef.current) return;
      const raw = response?.data?.data ?? response?.data ?? [];
      setCabinetPofCatalog(Array.isArray(raw) ? raw : []);
    } catch (_error) {
      if (seq !== loadCabinetPofCatalogSeqRef.current) return;
      setCabinetPofCatalog([]);
      message.error('Не удалось загрузить список отпечатков ОС из Kraken');
    } finally {
      if (seq === loadCabinetPofCatalogSeqRef.current) {
        setCabinetPofCatalogLoading(false);
      }
    }
  }, []);

  const cabinetPofCheckboxOptions = useMemo(
    () => (cabinetPofCatalog || []).map((row) => ({
      label: row.display_name || row.name || `ID ${row.id}`,
      value: Number(row.id),
    })).filter((opt) => Number.isFinite(opt.value) && opt.value > 0),
    [cabinetPofCatalog],
  );

  const handleCabinetPofSelectAll = useCallback((event) => {
    event?.preventDefault?.();
    const allIds = cabinetPofCheckboxOptions.map((opt) => opt.value);
    cabinetDefaultsForm.setFieldsValue({ cabinet_pof_visible_ids: allIds });
    markLkSectionDirty('fingerprints');
  }, [cabinetDefaultsForm, cabinetPofCheckboxOptions, markLkSectionDirty]);

  const handleTelegramBotProxyChange = useCallback((index, value) => {
    setCabinetTelegramBotProxies((prev) => {
      const next = [...prev];
      next[index] = value;
      cabinetTelegramBotProxiesRef.current = next;
      return next;
    });
    markLkSectionDirty('tgProxies');
  }, [markLkSectionDirty]);

  const handleAddTelegramBotProxy = useCallback(() => {
    setCabinetTelegramBotProxies((prev) => {
      if (prev.length >= MAX_TELEGRAM_BOT_PROXIES) {
        message.warning(`Не более ${MAX_TELEGRAM_BOT_PROXIES} прокси`);
        return prev;
      }
      const next = [...prev, ''];
      cabinetTelegramBotProxiesRef.current = next;
      return next;
    });
    markLkSectionDirty('tgProxies');
  }, [markLkSectionDirty]);

  const handleRemoveTelegramBotProxy = useCallback((index) => {
    setCabinetTelegramBotProxies((prev) => {
      const next = prev.filter((_, idx) => idx !== index);
      cabinetTelegramBotProxiesRef.current = next;
      return next;
    });
    markLkSectionDirty('tgProxies');
  }, [markLkSectionDirty]);

  const handleCabinetPofClearAll = useCallback((event) => {
    event?.preventDefault?.();
    cabinetDefaultsForm.setFieldsValue({ cabinet_pof_visible_ids: [] });
    markLkSectionDirty('fingerprints');
  }, [cabinetDefaultsForm, markLkSectionDirty]);

  const handleAddCabinetScript = useCallback(() => {
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setCabinetScripts((prev) => [
      ...prev,
      {
        id: tempId,
        name: '',
        content: '',
        pages: ['all'],
        tiers: ['all'],
        enabled: true,
        starts_at: null,
        ends_at: null,
      },
    ]);
    setCabinetScriptBodyExpanded((prev) => ({ ...prev, [tempId]: true }));
    markLkSectionDirty('scripts');
  }, [markLkSectionDirty]);

  const handleUpdateCabinetScript = useCallback((index, patch) => {
    markLkSectionDirty('scripts');
    setCabinetScripts((prev) => prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item)));
  }, [markLkSectionDirty]);

  const handleDeleteCabinetScript = useCallback((index) => {
    markLkSectionDirty('scripts');
    setCabinetScripts((prev) => prev.filter((_, idx) => idx !== index));
  }, [markLkSectionDirty]);

  const pickOperatorTemplatesFromResponse = (response) => (
    response?.data?.data?.templates
    ?? response?.data?.templates
    ?? []
  );

  const handleLoadOperatorTemplates = async () => {
    try {
      setOperatorsLoading(true);
      const response = await apiService.getOperatorTemplates();
      const templates = pickOperatorTemplatesFromResponse(response);
      const normalizedTemplates = Array.isArray(templates)
        ? templates.map((item) => ({
            ...item,
            patterns: Array.isArray(item?.patterns) ? item.patterns : [],
            patternsText: Array.isArray(item?.patterns) ? item.patterns.join('; ') : '',
            personal_cabinet_url: String(item?.personal_cabinet_url ?? ''),
          }))
        : [];
      setOperatorTemplates(normalizedTemplates);
    } catch (error) {
      message.error('Ошибка загрузки шаблонов операторов');
      setOperatorTemplates([]);
    } finally {
      setOperatorsLoading(false);
    }
  };

  const pickIntegrationsFromResponse = (response) => (
    response?.data?.data ?? response?.data ?? null
  );

  const applyIntegrationsFormFromData = useCallback((data) => {
    if (!data?.sections) {
      integrationsForm.resetFields();
      return;
    }
    const initial = {};
    data.sections.forEach((section) => {
      (section.fields || []).forEach((field) => {
        initial[field.key] = field.secret ? '' : (field.value || '');
      });
    });
    integrationsForm.setFieldsValue(initial);
  }, [integrationsForm]);

  const handleLoadIntegrationsSettings = async () => {
    try {
      setIntegrationsLoading(true);
      const response = await apiService.getIntegrationsSettings();
      const data = pickIntegrationsFromResponse(response);
      const normalized = data && typeof data === 'object' ? data : null;
      setIntegrationsSettings(normalized);
      applyIntegrationsFormFromData(normalized);
    } catch (error) {
      message.error('Ошибка загрузки настроек интеграций');
      setIntegrationsSettings(null);
      integrationsForm.resetFields();
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const buildIntegrationsSectionPayload = useCallback((section) => {
    const formValues = integrationsForm.getFieldsValue(true);
    const payload = {};
    (section.fields || []).forEach((field) => {
      const raw = formValues[field.key];
      const newVal = String(raw ?? '').trim();
      if (field.secret) {
        if (newVal && newVal !== INTEGRATIONS_SECRET_MASK) {
          payload[field.key] = newVal;
        }
        return;
      }
      payload[field.key] = newVal;
    });
    return payload;
  }, [integrationsForm]);

  const handleSaveIntegrationsSection = async (section) => {
    if (!section) return;
    const payload = buildIntegrationsSectionPayload(section);
    const hasNonSecretUpdate = (section.fields || []).some((field) => !field.secret && field.key in payload);
    const hasSecretUpdate = (section.fields || []).some((field) => field.secret && field.key in payload);
    if (!hasNonSecretUpdate && !hasSecretUpdate) {
      message.info('Нет изменений для сохранения');
      return;
    }
    try {
      setIntegrationsSavingSectionId(section.id);
      const response = await apiService.setIntegrationsSettings({ values: payload });
      const data = pickIntegrationsFromResponse(response);
      const normalized = data && typeof data === 'object' ? data : null;
      setIntegrationsSettings(normalized);
      applyIntegrationsFormFromData(normalized);
      message.success(`${section.title}: сохранено`);
    } catch (error) {
      message.error(parseApiErrorDetail(error) || 'Ошибка сохранения интеграций');
    } finally {
      setIntegrationsSavingSectionId(null);
    }
  };

  // SM1: «Проверить» проверяет сохранённое в .env, а не набранное в форме.
  const handleCheckIntegrationsSection = async (section) => {
    if (!section) return;
    try {
      setIntegrationsCheckingSectionId(section.id);
      setIntegrationsChecks((prev) => ({ ...prev, [section.id]: null }));
      const response = await apiService.checkIntegrationsSection(section.id);
      const data = pickIntegrationsFromResponse(response);
      setIntegrationsChecks((prev) => ({ ...prev, [section.id]: { result: data } }));
    } catch (error) {
      setIntegrationsChecks((prev) => ({
        ...prev,
        [section.id]: { error: parseApiErrorDetail(error) || 'Ошибка проверки' },
      }));
    } finally {
      setIntegrationsCheckingSectionId(null);
    }
  };

  const handleAddOperatorTemplate = () => {
    setOperatorTemplates((prev) => [...prev, { name: '', patterns: [], patternsText: '', personal_cabinet_url: '' }]);
  };

  const handleDeleteOperatorTemplate = (index) => {
    setOperatorTemplates((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOperatorTemplateNameChange = (index, value) => {
    setOperatorTemplates((prev) => prev.map((item, i) => (i === index ? { ...item, name: value } : item)));
  };

  const handleOperatorTemplatePatternsChange = (index, value) => {
    setOperatorTemplates((prev) => prev.map((item, i) => (
      i === index
        ? { ...item, patternsText: String(value || '') }
        : item
    )));
  };

  const handleOperatorTemplatePersonalCabinetUrlChange = (index, value) => {
    setOperatorTemplates((prev) => prev.map((item, i) => (
      i === index
        ? { ...item, personal_cabinet_url: String(value ?? '') }
        : item
    )));
  };

  const handleSaveOperatorTemplates = async () => {
    try {
      setOperatorsLoading(true);
      const payload = {
        templates: operatorTemplates.map((item) => ({
          name: String(item?.name || '').trim(),
          patterns: String(item?.patternsText ?? (Array.isArray(item?.patterns) ? item.patterns.join('; ') : ''))
            .split(/[;,]/)
            .map((part) => part.trim())
            .filter(Boolean),
          personal_cabinet_url: String(item?.personal_cabinet_url ?? '').trim(),
        })).filter((item) => item.name),
      };
      const response = await apiService.saveOperatorTemplates(payload);
      const saved = pickOperatorTemplatesFromResponse(response);
      const normalizedSaved = Array.isArray(saved)
        ? saved.map((item) => ({
            ...item,
            patterns: Array.isArray(item?.patterns) ? item.patterns : [],
            patternsText: Array.isArray(item?.patterns) ? item.patterns.join('; ') : '',
            personal_cabinet_url: String(item?.personal_cabinet_url ?? ''),
          }))
        : [];
      setOperatorTemplates(normalizedSaved);
      message.success('Шаблоны операторов сохранены');
    } catch (error) {
      message.error('Ошибка сохранения шаблонов операторов');
    } finally {
      setOperatorsLoading(false);
    }
  };

  const handlePreviewOperatorTemplates = async () => {
    try {
      setOperatorsLoading(true);
      const values = String(operatorsPreviewInput || '')
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
      if (values.length === 0) {
        setOperatorsPreviewResult([]);
        return;
      }
      const response = await apiService.previewOperatorTemplates(values);
      const resolved = response?.data?.data?.resolved || [];
      setOperatorsPreviewResult(Array.isArray(resolved) ? resolved : []);
    } catch (error) {
      message.error('Ошибка превью шаблонов');
      setOperatorsPreviewResult([]);
    } finally {
      setOperatorsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // Загружаем данные для резервного копирования безопасно
    try {
      fetchKrakenConnections().then((connections) => {
        handleLoadOsfpList(connections);
      });
      fetchAutoBackups();
      fetchFullBackups();
      fetchSSHConfig();
    } catch (error) {
      // Error initializing backup data
    }
    handleLoadOperatorTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleExportBackup = async () => {
    try {
      setBackupLoading(true);
      const connectionIds = allServers ? null : (selectedConnections.length > 0 ? selectedConnections : null);
      await apiService.exportProxiesBackup(connectionIds, allServers);
      message.success('Резервная копия успешно создана (прокси, клиенты, подключения, серверы)');
      fetchAutoBackups();
    } catch (error) {
      const errorMessage = await parseApiErrorDetail(error);
      message.error('Ошибка создания резервной копии: ' + errorMessage);
    } finally {
      setBackupLoading(false);
    }
  };
  
  const handleExportSimBackup = async () => {
    try {
      setSimBackupLoading(true);
      await apiService.exportSimCardsBackup();
      message.success('Резервная копия SIM-менеджера успешно выгружена');
    } catch (error) {
      message.error('Ошибка экспорта SIM-менеджера: ' + (error.message || 'Неизвестная ошибка'));
    } finally {
      setSimBackupLoading(false);
    }
  };

  const handleImportSimBackup = async (file) => {
    try {
      setSimBackupLoading(true);
      const response = await apiService.importSimCardsBackup(file);
      const {
        created = 0,
        updated = 0,
        skipped = 0,
        processed_rows = 0,
        total_rows = 0,
        errors: errs = null,
      } = response.data || {};
      const errorList = Array.isArray(errs) ? errs : [];

      if (errorList.length > 0) {
        Modal.warning({
          title: 'Импорт SIM-менеджера завершён с предупреждениями',
          width: 640,
          className: 'sett-import-result-modal',
          content: (
            <div style={{ color: '#e0e0e0' }}>
              <div style={{
                display: 'flex', gap: 16, flexWrap: 'wrap',
                padding: '10px 12px', marginBottom: 10,
                background: '#2a2a2a', borderRadius: 6, fontSize: 13,
              }}>
                <span>Строк: <b style={{ color: '#fff' }}>{total_rows || processed_rows}</b></span>
                <span>Создано: <b style={{ color: '#52c41a' }}>{created}</b></span>
                <span>Обновлено: <b style={{ color: '#1890ff' }}>{updated}</b></span>
                <span>Пропущено: <b style={{ color: '#faad14' }}>{skipped}</b></span>
              </div>
              <div style={{ maxHeight: 240, overflow: 'auto', fontSize: 12 }}>
                {errorList.slice(0, 50).map((err, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>{err}</div>
                ))}
                {errorList.length > 50 && (
                  <div style={{ color: '#999' }}>…и ещё {errorList.length - 50} ошибок</div>
                )}
              </div>
            </div>
          ),
        });
      } else {
        const parts = [];
        if (created > 0) parts.push(`создано: ${created}`);
        if (updated > 0) parts.push(`обновлено: ${updated}`);
        if (skipped > 0) parts.push(`пропущено: ${skipped}`);
        message.success(`Импорт SIM-менеджера — ${parts.join(', ') || 'без изменений'}`);
      }
    } catch (error) {
      message.error('Ошибка импорта SIM-менеджера: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSimBackupLoading(false);
    }
    return false;
  };

  const handleStopProxyImportProgressSimulation = () => {
    if (proxyImportSimTimerRef.current) {
      clearInterval(proxyImportSimTimerRef.current);
      proxyImportSimTimerRef.current = null;
    }
  };

  const handleCloseProxyImportProgress = () => {
    handleStopProxyImportProgressSimulation();
    setProxyImportProgress(null);
  };

  const handleStartProxyImportProgressSimulation = (serverPhaseStart, serverPhaseEnd, maxPercent = 92) => {
    handleStopProxyImportProgressSimulation();
    let activeIndex = serverPhaseStart;

    proxyImportSimTimerRef.current = setInterval(() => {
      setProxyImportProgress((prev) => {
        if (!prev) {
          return prev;
        }

        const phaseSpan = Math.max(1, serverPhaseEnd - serverPhaseStart + 1);
        const stepPercent = Math.round((maxPercent - 40) / phaseSpan);
        const nextPercent = Math.min(maxPercent, prev.percent + stepPercent);

        return advanceFullImportServerPhases(prev, activeIndex, nextPercent);
      });

      activeIndex += 1;
      if (activeIndex > serverPhaseEnd) {
        handleStopProxyImportProgressSimulation();
      }
    }, 4000);
  };

  const handleFinishProxyImportProgress = async () => {
    setProxyImportProgress((prev) => completeFullImportProgressState(prev));
    await new Promise((resolve) => {
      window.setTimeout(resolve, 700);
    });
    handleCloseProxyImportProgress();
  };

  const handleImportBackup = async (file) => {
    const uploadFile = file?.originFileObj || file;
    if (!uploadFile) {
      message.error('Файл резервной копии не выбран');
      return false;
    }

    const syncToKraken = backupForm.getFieldValue('sync_to_kraken') !== false;
    const importPhases = buildProxyBackupImportPhases(syncToKraken);

    try {
      setBackupLoading(true);
      setProxyImportProgress(createFullImportProgressState(
        importPhases,
        'Импорт резервной копии прокси',
        uploadFile.name,
      ));

      let uploadFinished = false;
      const response = await apiService.importProxiesBackup(
        uploadFile,
        syncToKraken,
        (event) => {
          setProxyImportProgress((prev) => {
            const next = applyFullImportUploadProgress(prev, event, 0.32);
            if (!uploadFinished && event.total && event.loaded >= event.total) {
              uploadFinished = true;
              handleStartProxyImportProgressSimulation(
                1,
                importPhases.length - 2,
                syncToKraken ? 94 : 92,
              );
            }
            return next;
          });
        },
      );

      handleStopProxyImportProgressSimulation();
      await handleFinishProxyImportProgress();
      
      const {
        imported = 0,
        updated = 0,
        errors: errs = [],
        total_processed = 0,
        synced_to_kraken = 0,
        synced_modems = 0,
        modem_settings_applied = 0,
        connections_imported = 0,
        clients_added = 0,
        clients_updated = 0,
        connection_notes: connectionNotes = [],
        server_notes: serverNotes = [],
        client_notes: clientNotes = [],
      } = response.data;
      const infoNotes = [...clientNotes, ...connectionNotes, ...serverNotes];
      const hasErrors = errs.length > 0;
      
      if (hasErrors) {
        Modal.warning({
          title: 'Импорт завершён с предупреждениями',
          width: 640,
          className: 'sett-import-result-modal',
          content: (
            <div style={{ color: '#e0e0e0' }}>
              <div style={{
                display: 'flex', gap: 16, flexWrap: 'wrap',
                padding: '10px 12px', marginBottom: 10,
                background: '#2a2a2a', borderRadius: 6, fontSize: 13
              }}>
                <span>Обработано: <b style={{ color: '#fff' }}>{total_processed}</b></span>
                <span>Создано: <b style={{ color: '#52c41a' }}>{imported}</b></span>
                <span>Обновлено: <b style={{ color: '#1890ff' }}>{updated}</b></span>
                <span>Прокси в Kraken: <b style={{ color: '#faad14' }}>{synced_to_kraken}</b></span>
                {(synced_modems > 0 || modem_settings_applied > 0) && (
                  <span>
                    Модемы: <b style={{ color: '#faad14' }}>{synced_modems}</b>
                    {modem_settings_applied > 0 ? ` (modem_*: ${modem_settings_applied})` : ''}
                  </span>
                )}
                {connections_imported > 0 && (
                  <span>Подключений: <b style={{ color: '#52c41a' }}>{connections_imported}</b></span>
                )}
                {(clients_added > 0 || clients_updated > 0) && (
                  <span>
                    Клиенты: <b style={{ color: '#52c41a' }}>+{clients_added}</b>
                    {clients_updated > 0 ? ` / ~${clients_updated}` : ''}
                  </span>
                )}
              </div>
              {infoNotes.length > 0 && (
                <div style={{
                  maxHeight: 160, overflow: 'auto', marginBottom: 10,
                  background: '#1a2a1a', borderRadius: 6, padding: 10,
                  border: '1px solid #2d4a2d'
                }}>
                  {infoNotes.map((note, i) => (
                    <div key={`note-${i}`} style={{ fontSize: 13, lineHeight: 1.5, color: '#b7eb8f', marginBottom: 4 }}>
                      {note}
                    </div>
                  ))}
                </div>
              )}
              <div style={{
                maxHeight: 320, overflow: 'auto',
                background: '#1e1e1e', borderRadius: 6, padding: 10,
                border: '1px solid #333'
              }}>
                {errs.map((err, i) => (
                  <div key={i} style={{
                    padding: '8px 10px', marginBottom: 6,
                    background: '#2a2020', borderRadius: 4,
                    borderLeft: '3px solid #f5222d',
                    fontSize: 13, lineHeight: 1.5, color: '#d9d9d9'
                  }}>
                    {err}
                  </div>
                ))}
              </div>
            </div>
          ),
        });
      } else {
        const connectionsMsg = connections_imported > 0
          ? `, подключений Kraken: ${connections_imported}`
          : '';
        const clientsMsg = (clients_added > 0 || clients_updated > 0)
          ? `, клиенты: +${clients_added}${clients_updated > 0 ? ` / ~${clients_updated}` : ''}`
          : '';
        message.success(
          `Импорт завершён: создано ${imported}, обновлено ${updated}${connectionsMsg}${clientsMsg}, отправлено в Kraken — прокси: ${synced_to_kraken}, модемы: ${synced_modems}`
        );
      }
      
      fetchAutoBackups();
      return false;
    } catch (error) {
      handleCloseProxyImportProgress();
      const errorMessage = await parseApiErrorDetail(error);
      message.error('Ошибка импорта резервной копии: ' + errorMessage);
      return false;
    } finally {
      setBackupLoading(false);
    }
  };
  
  const handleDownloadAutoBackup = async (filename) => {
    try {
      message.loading({ content: 'Скачивание...', key: 'autoBackupDownload', duration: 0 });
      await apiService.downloadAutoBackup(filename);
      message.success({ content: 'Резервная копия скачана', key: 'autoBackupDownload' });
    } catch (error) {
      message.error({
        content: 'Ошибка скачивания резервной копии: ' + (error.message || 'Неизвестная ошибка'),
        key: 'autoBackupDownload',
      });
    }
  };

  // ---- Full system backup handlers ----
  const fetchFullBackups = async () => {
    try {
      const response = await apiService.listFullBackups();
      setFullBackups(Array.isArray(response?.data?.backups) ? response.data.backups : []);
      setFullBackupPage(1);
    } catch {
      setFullBackups([]);
    }
  };

  const handleCreateFullBackup = async () => {
    try {
      setFullBackupLoading(true);
      message.loading({ content: 'Создание полного бэкапа...', key: 'fullBackup', duration: 0 });
      await apiService.createFullBackup();
      message.success({ content: 'Полный бэкап создан', key: 'fullBackup' });
      fetchFullBackups();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
      message.error({ content: 'Ошибка создания бэкапа: ' + msg, key: 'fullBackup' });
    } finally {
      setFullBackupLoading(false);
    }
  };

  const handleDownloadFullBackup = async (filename, source = 'auto') => {
    const isSshSource = source === 'ssh';
    try {
      message.loading({
        content: isSshSource ? 'Скачивание бэкапа с SSH...' : 'Скачивание бэкапа...',
        key: 'fullBackupDownload',
        duration: 0,
      });
      await apiService.downloadFullBackup(filename, source);
      message.success({ content: 'Бэкап скачан', key: 'fullBackupDownload' });
    } catch (error) {
      message.error({
        content: 'Ошибка скачивания: ' + (error.message || 'Неизвестная ошибка'),
        key: 'fullBackupDownload',
      });
    }
  };

  const buildFullRestoreStorageValidatePhases = (isSshSource = false) => {
    if (isSshSource) {
      return [{
        id: 'remote',
        label: 'Проверка на SSH-сервере',
        hint: 'Чтение метаданных архива (обычно несколько секунд)',
      }];
    }

    return [
      {
        id: 'fetch',
        label: 'Чтение архива',
        hint: 'Локальный каталог backups',
      },
      { id: 'validate', label: 'Проверка структуры', hint: 'Формат tar.gz и database.sql' },
      { id: 'analyze', label: 'Анализ содержимого', hint: 'Файлы, размер и предупреждения' },
    ];
  };

  const renderFullBackupRestoreConfirmContent = (validation, filename, restoreSystemConfigs) => {
    const warnings = Array.isArray(validation.warnings) ? validation.warnings : [];
    const sizeMb = validation.size ? (validation.size / 1024 / 1024).toFixed(1) : '?';

    return (
      <div className="sett-full-import-confirm">
        <p>
          Будет выполнено <strong>деструктивное восстановление</strong>: текущая база PostgreSQL
          и файлы проекта будут перезаписаны данными из архива.
        </p>
        <ul>
          <li>Файл: <strong>{validation.filename || filename}</strong></li>
          <li>Размер: <strong>{sizeMb} MB</strong></li>
          <li>Файлов в архиве: <strong>{validation.members_count ?? '—'}</strong></li>
          <li>database.sql: <strong>{validation.has_database ? 'да' : 'нет'}</strong></li>
        </ul>
        {warnings.length > 0 && (
          <div className="sett-full-import-confirm__warnings">
            <strong>Предупреждения:</strong>
            <ul>
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        )}
        {restoreSystemConfigs && (
          <p>
            Также будут восстановлены конфиги <strong>systemd</strong> и <strong>nginx</strong>.
          </p>
        )}
        {validation.quick_validation && (
          <p className="sett-full-import-confirm__hint">
            Выполнена быстрая проверка по размеру и имени файла. Полная проверка tar выполнится при восстановлении.
          </p>
        )}
        <p className="sett-full-import-confirm__hint">
          Рекомендуется создать свежий бэкап перед восстановлением. Процесс может занять до 15 минут.
        </p>
      </div>
    );
  };

  const handleRestoreListedFullBackup = async (record) => {
    const filename = record?.filename;
    const source = record?.source === 'ssh' ? 'ssh' : 'auto';
    const isSshSource = record?.source === 'ssh';

    if (!filename) {
      message.warning('Не указан файл бэкапа');
      return;
    }

    try {
      setFullImportLoading(true);
      setFullImportProgress(createFullImportProgressState(
        buildFullRestoreStorageValidatePhases(isSshSource),
        'Проверка архива',
        filename,
      ));
      handleStartFullImportWaitingProgress(88);

      const validateResponse = await apiService.validateFullBackupRestore(filename, source);

      handleStopFullImportProgressSimulation();
      await handleFinishFullImportProgress();

      const validation = validateResponse?.data?.validation || {};

      const handleRunListedFullBackupRestore = async () => {
        try {
          setFullImportLoading(true);
          setFullImportProgress(createFullImportProgressState(
            buildFullRestoreStorageRestorePhases(isSshSource),
            'Восстановление бэкапа',
            filename,
          ));
          handleStartFullImportWaitingProgress(94);

          const response = await apiService.restoreFullBackupFromStorage(
            filename,
            fullImportRestoreSystemConfigs,
            source,
          );

          handleStopFullImportProgressSimulation();
          await handleFinishFullImportProgress();

          const data = response?.data || {};
          const filesRestored = data.files?.files_restored ?? 0;
          message.success({
            content: `Восстановление завершено. Файлов: ${filesRestored}. Перезапустите backend при необходимости.`,
            duration: 8,
          });
          fetchFullBackups();
        } catch (error) {
          handleCloseFullImportProgress();
          const msg = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
          message.error({ content: 'Ошибка восстановления: ' + msg });
        } finally {
          setFullImportLoading(false);
        }
      };

      const confirmModal = Modal.confirm({
        title: 'Восстановить полный бэкап?',
        width: 640,
        okText: 'Восстановить',
        cancelText: 'Отмена',
        okButtonProps: { danger: true },
        content: renderFullBackupRestoreConfirmContent(
          validation,
          filename,
          fullImportRestoreSystemConfigs,
        ),
        onOk: () => {
          confirmModal.destroy();
          window.setTimeout(() => {
            void handleRunListedFullBackupRestore();
          }, 0);
        },
      });
    } catch (error) {
      handleCloseFullImportProgress();
      const msg = formatFullBackupArchiveError(error);
      message.error({ content: 'Ошибка проверки архива: ' + msg });
    } finally {
      setFullImportLoading(false);
    }
  };

  const handleDeleteFullBackup = async (filename) => {
    try {
      await apiService.deleteFullBackup(filename);
      message.success('Бэкап удалён');
      fetchFullBackups();
    } catch (error) {
      message.error('Ошибка удаления: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSelectFullImportFile = (file) => {
    if (!file?.name?.toLowerCase().endsWith('.tar.gz')) {
      message.error('Выберите архив .tar.gz (Proxy_Manager_full_*.tar.gz)');
      return Upload.LIST_IGNORE;
    }
    setFullImportFile(file);
    return false;
  };

  const handleClearFullImportFile = () => {
    setFullImportFile(null);
  };

  const handleStopFullImportProgressSimulation = () => {
    if (fullImportSimTimerRef.current) {
      clearInterval(fullImportSimTimerRef.current);
      fullImportSimTimerRef.current = null;
    }
  };

  const handleCloseFullImportProgress = () => {
    handleStopFullImportProgressSimulation();
    setFullImportProgress(null);
  };

  const handleStartFullImportProgressSimulation = (serverPhaseStart, serverPhaseEnd, maxPercent = 92) => {
    handleStopFullImportProgressSimulation();
    let activeIndex = serverPhaseStart;

    fullImportSimTimerRef.current = setInterval(() => {
      setFullImportProgress((prev) => {
        if (!prev) {
          return prev;
        }

        const phaseSpan = Math.max(1, serverPhaseEnd - serverPhaseStart + 1);
        const stepPercent = Math.round((maxPercent - 40) / phaseSpan);
        const nextPercent = Math.min(maxPercent, prev.percent + stepPercent);

        return advanceFullImportServerPhases(prev, activeIndex, nextPercent);
      });

      activeIndex += 1;
      if (activeIndex > serverPhaseEnd) {
        handleStopFullImportProgressSimulation();
      }
    }, 5000);
  };

  const handleStartFullImportWaitingProgress = (maxPercent = 88) => {
    handleStopFullImportProgressSimulation();
    fullImportSimTimerRef.current = setInterval(() => {
      setFullImportProgress((prev) => tickFullImportWaitingProgress(prev, maxPercent));
    }, 2000);
  };

  const handleFinishFullImportProgress = async () => {
    setFullImportProgress((prev) => completeFullImportProgressState(prev));
    await new Promise((resolve) => {
      window.setTimeout(resolve, 700);
    });
    handleCloseFullImportProgress();
  };

  const handleImportFullBackup = async () => {
    if (!fullImportFile) {
      message.warning('Сначала выберите файл архива');
      return;
    }

    try {
      setFullImportLoading(true);
      setFullImportProgress(createFullImportProgressState(
        FULL_IMPORT_VALIDATE_PHASES,
        'Проверка архива',
        fullImportFile.name,
      ));

      let uploadFinished = false;
      const validateResponse = await apiService.validateFullBackupImport(
        fullImportFile,
        (event) => {
          setFullImportProgress((prev) => {
            const next = applyFullImportUploadProgress(prev, event, 0.42);
            if (!uploadFinished && event.total && event.loaded >= event.total) {
              uploadFinished = true;
              handleStartFullImportProgressSimulation(1, FULL_IMPORT_VALIDATE_PHASES.length - 1, 88);
            }
            return next;
          });
        },
      );

      handleStopFullImportProgressSimulation();
      await handleFinishFullImportProgress();

      const validation = validateResponse?.data?.validation || {};
      const warnings = Array.isArray(validation.warnings) ? validation.warnings : [];
      const sizeMb = validation.size ? (validation.size / 1024 / 1024).toFixed(1) : '?';

      const handleRunFullBackupRestore = async () => {
        try {
          setFullImportLoading(true);
          setFullImportProgress(createFullImportProgressState(
            buildFullRestoreUploadRestorePhases(),
            'Восстановление бэкапа',
            fullImportFile.name,
          ));

          let restoreUploadFinished = false;
          const response = await apiService.importFullBackup(
            fullImportFile,
            fullImportRestoreSystemConfigs,
            (event) => {
              setFullImportProgress((prev) => {
                const next = applyFullImportUploadProgress(prev, event, 0.28);
                if (!restoreUploadFinished && event.total && event.loaded >= event.total) {
                  restoreUploadFinished = true;
                  handleStartFullImportWaitingProgress(94);
                }
                return next;
              });
            },
          );

          handleStopFullImportProgressSimulation();
          await handleFinishFullImportProgress();

          const data = response?.data || {};
          const filesRestored = data.files?.files_restored ?? 0;
          message.success({
            content: `Восстановление завершено. Файлов: ${filesRestored}. Перезапустите backend при необходимости.`,
            duration: 8,
          });
          setFullImportFile(null);
          fetchFullBackups();
        } catch (error) {
          handleCloseFullImportProgress();
          const msg = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
          message.error({ content: 'Ошибка восстановления: ' + msg });
        } finally {
          setFullImportLoading(false);
        }
      };

      const confirmModal = Modal.confirm({
        title: 'Восстановить полный бэкап?',
        width: 640,
        okText: 'Восстановить',
        cancelText: 'Отмена',
        okButtonProps: { danger: true },
        content: (
          <div className="sett-full-import-confirm">
            <p>
              Будет выполнено <strong>деструктивное восстановление</strong>: текущая база PostgreSQL
              и файлы проекта будут перезаписаны данными из архива.
            </p>
            <ul>
              <li>Файл: <strong>{validation.filename || fullImportFile.name}</strong></li>
              <li>Размер: <strong>{sizeMb} MB</strong></li>
              <li>Файлов в архиве: <strong>{validation.members_count ?? '—'}</strong></li>
              <li>database.sql: <strong>{validation.has_database ? 'да' : 'нет'}</strong></li>
            </ul>
            {warnings.length > 0 && (
              <div className="sett-full-import-confirm__warnings">
                <strong>Предупреждения:</strong>
                <ul>
                  {warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            {fullImportRestoreSystemConfigs && (
              <p>
                Также будут восстановлены конфиги <strong>systemd</strong> и <strong>nginx</strong>.
              </p>
            )}
            <p className="sett-full-import-confirm__hint">
              Рекомендуется создать свежий бэкап перед восстановлением. Процесс может занять до 15 минут.
            </p>
          </div>
        ),
        onOk: () => {
          confirmModal.destroy();
          window.setTimeout(() => {
            void handleRunFullBackupRestore();
          }, 0);
        },
      });
    } catch (error) {
      handleCloseFullImportProgress();
      const msg = formatFullBackupArchiveError(error);
      message.error({ content: 'Ошибка проверки архива: ' + msg });
    } finally {
      setFullImportLoading(false);
    }
  };

  const handleOpenSSHTransfer = (filename) => {
    setSshTransferFile(filename);
    sshForm.resetFields();
    setSshModalVisible(true);
  };

  const handleSSHTransfer = async (values) => {
    try {
      setSshTransferLoading(true);
      await apiService.transferBackupSSH({ filename: sshTransferFile, ...values });
      message.success('Бэкап передан на удалённый сервер');
      setSshModalVisible(false);
    } catch (error) {
      message.error('Ошибка передачи: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSshTransferLoading(false);
    }
  };

  // ---- SSH Config handlers ----
  const fetchSSHConfig = useCallback(async () => {
    try {
      const response = await apiService.getBackupSSHConfig();
      setSshConfig(response?.data?.config || null);
    } catch {
      setSshConfig(null);
    }
  }, []);

  const handleOpenSSHConfigModal = () => {
    const timeFromConfig = (t) => {
      if (!t || typeof t !== 'string') return dayjs('03:00', 'HH:mm');
      const d = dayjs(t, 'HH:mm');
      return d.isValid() ? d : dayjs('03:00', 'HH:mm');
    };
    if (sshConfig) {
      sshConfigForm.setFieldsValue({
        ssh_host: sshConfig.ssh_host,
        ssh_port: sshConfig.ssh_port,
        ssh_username: sshConfig.ssh_username,
        remote_path: sshConfig.remote_path,
        auto_backup_enabled: sshConfig.auto_backup_enabled,
        auto_backup_interval_days: sshConfig.auto_backup_interval_days,
        auto_backup_time: timeFromConfig(sshConfig.auto_backup_time),
        ssh_password: '',
        ssh_private_key: '',
      });
    } else {
      sshConfigForm.resetFields();
      sshConfigForm.setFieldsValue({
        ssh_port: 22,
        ssh_username: 'root',
        remote_path: '/root/backups/',
        auto_backup_enabled: true,
        auto_backup_interval_days: 2,
        auto_backup_time: dayjs('03:00', 'HH:mm'),
      });
    }
    setSshConfigModalVisible(true);
  };

  const handleSaveSSHConfig = async (values) => {
    try {
      setSshConfigLoading(true);
      const payload = { ...values };
      if (payload.auto_backup_time && typeof payload.auto_backup_time.format === 'function') {
        payload.auto_backup_time = payload.auto_backup_time.format('HH:mm');
      } else if (payload.auto_backup_time == null) {
        payload.auto_backup_time = '03:00';
      }
      if (!payload.ssh_password) delete payload.ssh_password;
      if (!payload.ssh_private_key) delete payload.ssh_private_key;
      const response = await apiService.saveBackupSSHConfig(payload);
      setSshConfig(response?.data?.config || null);
      const installed = response?.data?.ssh_key_installed;
      if (installed === true) {
        message.success(
          'SSH-сервер сохранён: ключ добавлен в authorized_keys, каталог для бэкапов создан при необходимости'
        );
      } else {
        message.success('SSH-сервер сохранён');
        if (installed === false && response?.data?.ssh_install_error) {
          message.warning('Не удалось поставить ключ на сервер: ' + response.data.ssh_install_error);
        } else if (installed === null && response?.data?.ssh_install_message) {
          message.info(response.data.ssh_install_message);
        }
      }
      setSshConfigModalVisible(false);
      fetchFullBackups();
    } catch (error) {
      message.error('Ошибка сохранения: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSshConfigLoading(false);
    }
  };

  const handleDeleteSSHConfig = async () => {
    try {
      await apiService.deleteBackupSSHConfig();
      setSshConfig(null);
      fetchFullBackups();
      message.success('SSH-конфигурация удалена');
    } catch (error) {
      message.error('Ошибка удаления: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTestSSHConnection = async () => {
    try {
      setSshTestLoading(true);
      const response = await apiService.testBackupSSHConnection();
      message.success(response?.data?.message || 'Подключение успешно');
    } catch (error) {
      message.error('Ошибка: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSshTestLoading(false);
    }
  };

  const handleGenerateSSHKey = async () => {
    try {
      setSshKeyGenLoading(true);
      const response = await apiService.generateBackupSSHKey();
      if (response?.data?.ssh_key_installed === true) {
        message.success('Новая пара ключей сгенерирована и публичный ключ установлен на сервер');
      } else {
        message.success('SSH-ключ сгенерирован');
        if (response?.data?.ssh_key_installed === false && response?.data?.ssh_install_error) {
          message.warning('Установка ключа: ' + response.data.ssh_install_error);
        }
      }
      if (response?.data?.public_key) {
        setSshConfig(prev => prev ? { ...prev, ssh_public_key: response.data.public_key, has_private_key: true } : prev);
      }
      fetchSSHConfig();
    } catch (error) {
      message.error('Ошибка: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSshKeyGenLoading(false);
    }
  };

  const handleInstallSSHKey = async () => {
    try {
      setSshKeyInstallLoading(true);
      await apiService.installBackupSSHKey();
      message.success('SSH-ключ установлен на сервер');
    } catch (error) {
      message.error('Ошибка: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSshKeyInstallLoading(false);
    }
  };

  const handleTransferToConnected = async (filename) => {
    try {
      message.loading({ content: 'Передача на сервер...', key: 'sshTransfer', duration: 0 });
      await apiService.transferToConnectedServer(filename);
      message.success({ content: 'Бэкап передан на сервер', key: 'sshTransfer' });
    } catch (error) {
      message.error({ content: 'Ошибка: ' + (error.response?.data?.detail || error.message), key: 'sshTransfer' });
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || 'Недостаточно прав. Требуются права администратора';
        message.error(detail);
      } else {
        const raw = error.response?.data?.detail ?? error.message;
        const text = Array.isArray(raw)
          ? raw.map((x) => (x && typeof x === 'object' && x.msg ? x.msg : String(x))).join('; ')
          : String(raw || '');
        message.error(text || 'Ошибка загрузки списка пользователей');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      full_name: user.full_name || '',
      role: user.role?.value || user.role || 'user',
      telegram_username: user.telegram_username || '',
      telegram_chat_id: user.telegram_chat_id != null ? String(user.telegram_chat_id) : '',
    });
    setModalVisible(true);
  };

  const handleOpenCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      role: 'user'
    });
    setModalVisible(true);
  };

  const handleCreateUser = async (values) => {
    try {
      // Подготавливаем данные для отправки
      const userData = {
        username: (values.username || '').trim(),
        email: (values.email || '').trim(),
        password: values.password,
        role: (values.role || 'user').trim().toLowerCase()  // Роль передается как строка в нижнем регистре
      };
      
      // Проверяем обязательные поля
      if (!userData.username) {
        message.error('Имя пользователя обязательно');
        return;
      }
      if (!userData.email) {
        message.error('Email обязателен');
        return;
      }
      if (!userData.password) {
        message.error('Пароль обязателен');
        return;
      }
      
      // full_name передаем только если оно заполнено и не пустое
      if (values.full_name && values.full_name.trim()) {
        userData.full_name = values.full_name.trim();
      }

      const tgUsername = (values.telegram_username || '').trim();
      userData.telegram_username = tgUsername || null;
      const tgChatRaw = String(values.telegram_chat_id ?? '').trim();
      userData.telegram_chat_id = tgChatRaw ? parseInt(tgChatRaw, 10) : null;
      
      await apiService.createUser(userData);
      message.success('Пользователь успешно создан');
      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      if (error.response?.status === 422) {
        // Ошибка валидации
        const responseData = error.response.data;
        const details = responseData?.detail;
        const messageText = responseData?.message;
        
        // Используем форматированное сообщение если есть
        if (messageText) {
          message.error(`Ошибка валидации: ${messageText}`, 10);
        } else if (Array.isArray(details)) {
          // Форматируем ошибки валидации Pydantic
          const errors = details.map(d => {
            const field = d.loc && d.loc.length > 0 ? d.loc.slice(1).join('.') : 'unknown';
            const msg = d.msg || d.message || JSON.stringify(d);
            return `${field}: ${msg}`;
          }).join('; ');
          message.error(`Ошибка валидации: ${errors}`, 10); // Показываем 10 секунд
        } else if (typeof details === 'string') {
          message.error(details, 10);
        } else {
          message.error(`Ошибка валидации данных: ${JSON.stringify(responseData)}`, 10);
        }
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail || 'Ошибка создания пользователя';
        message.error(detail);
      } else {
        message.error('Ошибка создания пользователя');
      }
    }
  };

  const handleUpdateUser = async (values) => {
    if (!editingUser) return;
    
    try {
      // Подготавливаем данные для отправки
      const userData = {
        username: (values.username || '').trim(),
        email: (values.email || '').trim(),
        role: (values.role || editingUser.role?.value || editingUser.role || 'user').trim().toLowerCase()
      };
      
      // Пароль обновляем только если он указан
      if (values.password && values.password.trim()) {
        userData.password = values.password;
      }
      
      // full_name передаем только если оно заполнено и не пустое
      if (values.full_name !== undefined) {
        if (values.full_name && values.full_name.trim()) {
          userData.full_name = values.full_name.trim();
        } else {
          userData.full_name = null;
        }
      }

      if (values.telegram_username !== undefined) {
        const tgUsername = (values.telegram_username || '').trim();
        userData.telegram_username = tgUsername || null;
      }
      if (values.telegram_chat_id !== undefined) {
        const tgChatRaw = String(values.telegram_chat_id ?? '').trim();
        userData.telegram_chat_id = tgChatRaw ? parseInt(tgChatRaw, 10) : null;
      }
      
      // Проверяем обязательные поля
      if (!userData.username) {
        message.error('Имя пользователя обязательно');
        return;
      }
      if (!userData.email) {
        message.error('Email обязателен');
        return;
      }
      
      await apiService.updateUser(editingUser.id, userData);
      message.success('Пользователь успешно обновлен');
      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      if (error.response?.status === 422) {
        const responseData = error.response.data;
        const details = responseData?.detail;
        const messageText = responseData?.message;
        
        if (messageText) {
          message.error(`Ошибка валидации: ${messageText}`, 10);
        } else if (Array.isArray(details)) {
          const errors = details.map(d => {
            const field = d.loc && d.loc.length > 0 ? d.loc.slice(1).join('.') : 'unknown';
            const msg = d.msg || d.message || JSON.stringify(d);
            return `${field}: ${msg}`;
          }).join('; ');
          message.error(`Ошибка валидации: ${errors}`, 10);
        } else if (typeof details === 'string') {
          message.error(details, 10);
        } else {
          message.error(`Ошибка валидации данных: ${JSON.stringify(responseData)}`, 10);
        }
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail || 'Ошибка обновления пользователя';
        message.error(detail);
      } else {
        message.error('Ошибка обновления пользователя');
      }
    }
  };

  const handleDeleteUser = async (userId, username) => {
    try {
      await apiService.deleteUser(userId);
      message.success(`Пользователь ${username} успешно удален`);
      fetchUsers();
    } catch (error) {
      if (error.response?.status === 400) {
        message.error(error.response.data?.detail || 'Нельзя удалить этого пользователя');
      } else if (error.response?.status === 403) {
        message.error('Недостаточно прав');
      } else {
        message.error('Ошибка удаления пользователя');
      }
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Администратор',
      'manager': 'Менеджер',
      'user': 'Пользователь',
      'demo': 'Демо',
      'viewer': 'Наблюдатель'
    };
    return labels[role] || role;
  };

  // Tab state
  const [activeTab, setActiveTab] = useState('users');
  const activeTabRef = useRef('users');

  useEffect(() => {
    if (isClientCabinetSection) {
      setActiveTab('lk-defaults');
      return;
    }

    if (isLogsSection) {
      setActiveTab((previousTab) => (
        isLogsSectionTab(previousTab)
          ? previousTab
          : 'logs'
      ));
      return;
    }

    if (isBackupsSection) {
      setActiveTab('backup');
      return;
    }

    setActiveTab((previousTab) => {
      if (
        previousTab === 'lk-defaults'
        || isLogsSectionTab(previousTab)
        || previousTab === 'backup'
      ) {
        return 'users';
      }
      return previousTab;
    });
  }, [isClientCabinetSection, isLogsSection, isBackupsSection]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const formatDate = useCallback((date) => {
    if (!date) return '—';
    try {
      const parsedDate = parseMskInstant(date);
      if (!parsedDate) {
        return String(date);
      }

      const parts = new Intl.DateTimeFormat('ru-RU', {
        timeZone: 'Europe/Moscow',
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour12: false,
      }).formatToParts(parsedDate);
      const getPart = (type) => parts.find((part) => part.type === type)?.value ?? '';

      return `${getPart('hour')}:${getPart('minute')} ${getPart('day')}.${getPart('month')}.${getPart('year')}`;
    } catch {
      return String(date);
    }
  }, []);

  const formatLogTimestamp = useCallback((value) => {
    if (!value) return '—';
    const formatted = formatMskDateTime(value);
    return formatted === '—' ? String(value) : formatted;
  }, []);

  const getRoleBadgeClass = useCallback((role) => {
    const map = { admin: 'red', manager: 'yellow', user: 'green', demo: 'blue', viewer: 'grey' };
    return map[role] || 'grey';
  }, []);

  /* ---------- System logs helpers ---------- */
  const LOGS_PAGE_SIZE = 100;

  const fetchSystemLogs = useCallback(async (page = 1, filters = logsFilters) => {
    setLogsLoading(true);
    try {
      const params = { limit: LOGS_PAGE_SIZE, offset: (page - 1) * LOGS_PAGE_SIZE };
      if (filters.category) params.category = filters.category;
      if (filters.level) params.level = filters.level;
      if (filters.search) params.search = filters.search;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.log_client && String(filters.log_client).trim()) {
        params.log_client = String(filters.log_client).trim();
      }
      if (filters.log_server && String(filters.log_server).trim()) {
        params.log_server = String(filters.log_server).trim();
      }
      if (filters.log_modem && String(filters.log_modem).trim()) {
        params.log_modem = String(filters.log_modem).trim();
      }
      if (filters.log_port && String(filters.log_port).trim()) {
        params.log_port = String(filters.log_port).trim();
      }

      const res = await apiService.getSystemLogs(params);
      const data = res?.data || {};
      setSystemLogs(data.logs || []);
      setLogsTotal(data.total || 0);
    } catch (err) {
      message.error('Ошибка загрузки логов');
    } finally {
      setLogsLoading(false);
    }
  }, [logsFilters]);

  const handleLogsFilterChange = useCallback((key, value) => {
    setLogsFilters((prev) => {
      const next = { ...prev, [key]: value };
      setLogsPage(1);
      fetchSystemLogs(1, next);
      return next;
    });
  }, [fetchSystemLogs]);

  const handleApplyModemLogsPreset = useCallback((errorsOnly = false) => {
    setLogsFilters((prev) => {
      const next = {
        ...prev,
        category: 'modem',
        level: errorsOnly ? 'error' : '',
      };
      setLogsPage(1);
      fetchSystemLogs(1, next);
      return next;
    });
  }, [fetchSystemLogs]);

  const handleApplyMismatchLogsPreset = useCallback((errorsOnly = false) => {
    setLogsFilters((prev) => {
      const next = {
        ...prev,
        category: 'mismatch',
        level: errorsOnly ? 'error' : '',
      };
      setLogsPage(1);
      fetchSystemLogs(1, next);
      return next;
    });
  }, [fetchSystemLogs]);

  const handleClearOldLogs = useCallback(async () => {
    try {
      const res = await apiService.clearSystemLogs(30);
      message.success(`Удалено ${res?.data?.deleted ?? 0} записей старше 30 дней`);
      fetchSystemLogs(logsPage);
    } catch {
      message.error('Ошибка очистки логов');
    }
  }, [fetchSystemLogs, logsPage]);

  useEffect(() => {
    if (isLogsSection && activeTab === 'logs') {
      fetchSystemLogs(logsPage);
    }
    if (activeTab === 'modem-defaults') {
      handleLoadModemDefaults();
      handleLoadModemResync();
    }
    if (activeTab === 'integrations') {
      handleLoadIntegrationsSettings();
    }
    if (activeTab === 'lk-defaults') {
      if (!Object.values(lkSectionDirtyRef.current).some(Boolean)) {
        handleLoadCabinetDefaults();
      }
      if (clientCabinetTab === 'main') {
        handleLoadCabinetPofCatalog();
        handleLoadKrakenCleanup();
      }
      if (clientCabinetTab === 'tg-proxies') {
        refreshTelegramBotProxyHealth();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, clientCabinetTab, isLogsSection]);

  /* CATEGORY_LABELS and LEVEL_LABELS are defined at module level above */

  const logsColumns = useMemo(() => [
    {
      title: 'Время',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 192,
      render: (v) => <span style={{ color: 'var(--pm-text-muted)' }}>{formatLogTimestamp(v)}</span>,
    },
    {
      title: 'Уровень',
      dataIndex: 'level',
      key: 'level',
      width: 112,
      render: (v) => <span className={`sett-log-badge sett-log-badge--${v}`}>{LEVEL_LABELS[v] || v}</span>,
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      width: 148,
      render: (v) => <span className={`sett-log-badge sett-log-badge--${v}`}>{CATEGORY_LABELS[v] || v}</span>,
    },
    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      width: 148,
    },
    {
      title: 'Клиент',
      dataIndex: 'log_client',
      key: 'log_client',
      width: 120,
      render: (v) => <span style={{ color: 'var(--pm-text-muted)' }}>{v || '—'}</span>,
    },
    {
      title: 'Подключение',
      dataIndex: 'log_server',
      key: 'log_server',
      width: 140,
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip placement="topLeft" title={v || ''} overlayStyle={{ maxWidth: 400 }}>
          <span style={{ color: 'var(--pm-text-muted)' }}>{v || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Номер модема',
      dataIndex: 'log_modem',
      key: 'log_modem',
      width: 112,
      render: (v) => <span style={{ color: 'var(--pm-text-muted)' }}>{v || '—'}</span>,
    },
    {
      title: 'Порт',
      dataIndex: 'log_port',
      key: 'log_port',
      width: 72,
      render: (v) => <span style={{ color: 'var(--pm-text-muted)' }}>{v || '—'}</span>,
    },
    {
      title: 'Сообщение',
      dataIndex: 'message',
      key: 'message',
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip placement="topLeft" title={v} overlayStyle={{ maxWidth: 520 }}>
          <span>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Пользователь',
      dataIndex: 'username',
      key: 'username',
      width: 148,
      render: (v) => <span style={{ color: 'var(--pm-text-muted)' }}>{v || '—'}</span>,
    },
  ], [formatLogTimestamp]);

  const columns = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 112,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Логин',
      dataIndex: 'username',
      key: 'username',
      width: 192,
      sorter: (a, b) => (a.username || '').localeCompare(b.username || ''),
    },
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => text || '—',
      sorter: (a, b) => (a.full_name || '').localeCompare(b.full_name || ''),
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      width: 192,
      sorter: (a, b) => (a.role || '').localeCompare(b.role || ''),
      render: (role) => (
        <span className={`sett-badge sett-badge--${getRoleBadgeClass(role)}`}>
          {getRoleLabel(role)}
        </span>
      ),
    },
    {
      title: 'Статус',
      key: 'is_active',
      width: 192,
      sorter: (a, b) => Number(a.is_active) - Number(b.is_active),
      render: (_, record) => (
        <span className={`sett-badge sett-badge--${record.is_active ? 'green' : 'red'}`}>
          {record.is_active ? 'Активен' : 'Пассивен'}
        </span>
      ),
    },
    {
      title: 'Последний вход',
      dataIndex: 'last_login',
      key: 'last_login',
      width: 192,
      sorter: (a, b) => new Date(a.last_login || 0) - new Date(b.last_login || 0),
      render: (date) => <span className="sett-cell--muted">{formatDate(date)}</span>,
    },
    {
      title: 'Дата создания',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 192,
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (date) => <span className="sett-cell--muted">{formatDate(date)}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      align: 'center',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'edit',
            label: 'Редактировать',
            icon: <EditOutlined />,
            onClick: () => handleOpenEditModal(record),
          },
          { type: 'divider' },
          {
            key: 'delete',
            label: (
              <Popconfirm
                title={`Удалить пользователя ${record.username}?`}
                description="Это действие нельзя отменить."
                onConfirm={() => handleDeleteUser(record.id, record.username)}
                okText="Да"
                cancelText="Нет"
                okButtonProps={{ danger: true }}
                onCancel={(e) => e?.stopPropagation()}
              >
                <span>Удалить</span>
              </Popconfirm>
            ),
            icon: <DeleteOutlined />,
            danger: true,
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="pm-action-dropdown"
          >
            <button
              className="pm-action-dots"
              aria-label="Действия"
              tabIndex={0}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="pm-action-dots__dot" />
              <span className="pm-action-dots__dot" />
              <span className="pm-action-dots__dot" />
            </button>
          </Dropdown>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [formatDate, getRoleBadgeClass]);

  const [settPage, setSettPage] = useState(1);
  const [settPerPage, setSettPerPage] = useState(50);
  const settPagination = useMemo(() => ({
    current: settPage,
    pageSize: settPerPage,
    showSizeChanger: false,
    className: 'sett-pagination-hidden',
  }), [settPage, settPerPage]);

  return (
    <div>
      {/* ===== Header — Figma ===== */}
      <div className="sett-header">
        <h1 className="sett-header__title">{settingsPageTitle}</h1>

        {!isClientCabinetSection && !isLogsSection && !isBackupsSection && (
        <div className="sett-header__tabs">
          <button
            type="button"
            className={`sett-chip${activeTab === 'users' ? ' sett-chip--active' : ' sett-chip--outline'}`}
            onClick={() => setActiveTab('users')}
            tabIndex={0}
            aria-label="Пользователи"
          >
            <span className="sett-chip__label">Пользователи</span>
            <span className="sett-chip__value">{users.length}</span>
          </button>
          <button
            type="button"
            className={`sett-chip${activeTab === 'modem-defaults' ? ' sett-chip--active' : ' sett-chip--outline'}`}
            onClick={() => setActiveTab('modem-defaults')}
            tabIndex={0}
            aria-label="Настройки модемов"
          >
            <span className="sett-chip__label">Модемы</span>
          </button>
          <button
            type="button"
            className={`sett-chip${activeTab === 'operators' ? ' sett-chip--active' : ' sett-chip--outline'}`}
            onClick={() => setActiveTab('operators')}
            tabIndex={0}
            aria-label="Операторы"
          >
            <span className="sett-chip__label">Операторы</span>
          </button>
          <button
            type="button"
            className={`sett-chip${activeTab === 'proxy-check-rules' ? ' sett-chip--active' : ' sett-chip--outline'}`}
            onClick={() => setActiveTab('proxy-check-rules')}
            tabIndex={0}
            aria-label="Правила проверки"
          >
            <span className="sett-chip__label">Правила проверки</span>
          </button>
          <button
            type="button"
            className={`sett-chip${activeTab === 'integrations' ? ' sett-chip--active' : ' sett-chip--outline'}`}
            onClick={() => setActiveTab('integrations')}
            tabIndex={0}
            aria-label="Интеграции"
          >
            <span className="sett-chip__label">Интеграции</span>
          </button>
        </div>
        )}

        {isLogsSection && (
        <div className="sett-header__tabs" aria-label="Разделы логов">
          {LOGS_SECTION_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`sett-chip${activeTab === tab.key ? ' sett-chip--active' : ' sett-chip--outline'}`}
              onClick={() => setActiveTab(tab.key)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                setActiveTab(tab.key);
              }}
              tabIndex={0}
              aria-label={tab.label}
              aria-current={activeTab === tab.key ? 'page' : undefined}
            >
              <span className="sett-chip__label">{tab.label}</span>
            </button>
          ))}
        </div>
        )}

        {isClientCabinetSection && (
        <div className="sett-header__tabs" aria-label="Разделы кабинета клиента">
          {CLIENT_CABINET_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`sett-chip${clientCabinetTab === tab.key ? ' sett-chip--active' : ' sett-chip--outline'}`}
              onClick={() => setClientCabinetTab(tab.key)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                setClientCabinetTab(tab.key);
              }}
              tabIndex={0}
              aria-label={tab.label}
              aria-current={clientCabinetTab === tab.key ? 'page' : undefined}
            >
              <span className="sett-chip__label">{tab.label}</span>
            </button>
          ))}
        </div>
        )}

        <div className="sett-header__actions">
          {activeTab === 'users' && (
            <>
              <button
                type="button"
                className={`sett-header__action-btn sett-header__action-btn--secondary${loading ? ' sett-header__action-btn--loading' : ''}`}
                onClick={fetchUsers}
                title="Обновить"
                aria-label="Обновить"
                tabIndex={0}
              >
                <RefreshIcon />
              </button>
              <button
                type="button"
                className="sett-header__action-btn sett-header__action-btn--primary"
                onClick={handleOpenCreateModal}
                title="Добавить пользователя"
                aria-label="Добавить пользователя"
                tabIndex={0}
              >
                <PlusIcon />
              </button>
            </>
          )}
          {isLogsSection && activeTab === 'logs' && (
            <>
              <button
                type="button"
                className={`sett-header__action-btn sett-header__action-btn--secondary${logsLoading ? ' sett-header__action-btn--loading' : ''}`}
                onClick={() => fetchSystemLogs(logsPage)}
                title="Обновить"
                aria-label="Обновить логи"
                tabIndex={0}
              >
                <RefreshIcon />
              </button>
            </>
          )}
          {isLogsSection && activeTab === 'balance-operations-logs' && (
            <button
              type="button"
              className="sett-header__action-btn sett-header__action-btn--secondary"
              onClick={() => setBalanceOpsRefreshNonce((prev) => prev + 1)}
              title="Обновить"
              aria-label="Обновить логи по операциям"
              tabIndex={0}
            >
              <RefreshIcon />
            </button>
          )}
          {activeTab === 'integrations' && (
            <button
              type="button"
              className={`sett-header__action-btn sett-header__action-btn--secondary${integrationsLoading ? ' sett-header__action-btn--loading' : ''}`}
              onClick={handleLoadIntegrationsSettings}
              title="Обновить"
              aria-label="Обновить интеграции"
              tabIndex={0}
            >
              <RefreshIcon />
            </button>
          )}
          {activeTab === 'lk-defaults' && isClientCabinetSection && (
            <>
              <button
                type="button"
                className={`sett-header__action-btn sett-header__action-btn--secondary${cabinetDefaultsLoading ? ' sett-header__action-btn--loading' : ''}`}
                onClick={() => handleLoadCabinetDefaults({ force: true })}
                title="Обновить"
                aria-label="Обновить настройки ЛК"
                tabIndex={0}
              >
                <RefreshIcon />
              </button>
            </>
          )}
        </div>
      </div>

      {/* ===== Tab content ===== */}
      {activeTab === 'users' && (
        <div className="sett-content">
          <div className="sett-table">
            <Table
              columns={columns}
              dataSource={users}
              loading={loading}
              rowKey="id"
              rowSelection={{ type: 'checkbox' }}
              scroll={{ x: 1200 }}
              size="middle"
              pagination={settPagination}
              onChange={(pag) => { if (pag) { setSettPage(pag.current); setSettPerPage(pag.pageSize); } }}
            />
            <CustomPagination
              currentPage={settPage}
              totalPages={Math.max(1, Math.ceil((users?.length || 0) / settPerPage))}
              totalItems={users?.length || 0}
              perPage={settPerPage}
              perPageOptions={[5, 10, 20, 50]}
              onPageChange={(p) => setSettPage(p)}
              onPerPageChange={(v) => { setSettPerPage(v); setSettPage(1); }}
            />
          </div>
        </div>
      )}

      {activeTab === 'backup' && (
        <div className="sett-tab-content">
          {/* Info banner */}
          <div className="sett-info">
            <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
            <div className="sett-info__body">
              <p className="sett-info__title">Резервное копирование прокси</p>
              <p className="sett-info__desc">
                Создавайте резервные копии прокси в формате Excel с возможностью фильтрации по серверам Kraken.
                Автоматический экспорт выполняется каждые 2 часа (все серверы Kraken), хранится 50 последних копий.
                При включённом SSH также выгружается ежедневный экспорт SIM вместе с полным бэкапом системы
                ({sshConfig?.auto_backup_time || '03:00'}, время сервера).
              </p>
            </div>
          </div>

          {/* Export card */}
          <div className="sett-card">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Экспорт резервной копии прокси</h3>
            </div>
            <div className="sett-card__body">
              <Form form={backupForm} layout="vertical">
                <Form.Item style={{ marginBottom: 12 }}>
                  <Checkbox
                    checked={allServers}
                    onChange={(e) => {
                      setAllServers(e.target.checked);
                      if (e.target.checked) setSelectedConnections([]);
                    }}
                  >
                    Все серверы Kraken
                  </Checkbox>
                </Form.Item>

                {!allServers && (
                  <Form.Item label="Серверы подключения" style={{ marginBottom: 16 }}>
                    <Select
                      mode="multiple"
                      placeholder="Выберите серверы подключения"
                      value={selectedConnections}
                      onChange={setSelectedConnections}
                      style={{ width: '100%' }}
                    >
                      {(Array.isArray(krakenConnections) ? krakenConnections : [])
                        .filter(conn => conn && conn.is_active)
                        .map(conn => (
                          <Option key={conn.id} value={conn.id}>
                            {conn.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                )}

                <Form.Item style={{ marginBottom: 0 }}>
                  <p className="sett-backup-export-hint">
                    Лист «Прокси» содержит все поля: тариф, лимитеры (slimiter/rlimiter/maxconn),
                    keep-alive, сжатие, Kraken allow/targetport, настройки модема (reconnect, OSFP),
                    cabinet, network port и др. Листы «Подключения» и «Серверы» — для восстановления Kraken при импорте.
                  </p>
                  <button
                    type="button"
                    className={`sett-form-btn sett-form-btn--primary${backupLoading ? ' sett-form-btn--loading' : ''}`}
                    onClick={handleExportBackup}
                    disabled={backupLoading}
                  >
                    <DownloadOutlined />
                    Выгрузить резервную копию
                  </button>
                </Form.Item>
              </Form>
            </div>
          </div>

          {/* Import card */}
          <div className="sett-card">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Импорт резервной копии прокси</h3>
            </div>
            <div className="sett-card__body">
              <Form form={backupForm} layout="vertical">
                <Form.Item name="sync_to_kraken" valuePropName="checked" initialValue={true} style={{ marginBottom: 12 }}>
                  <Checkbox>Отправить после импорта в Кракен</Checkbox>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Upload
                    accept=".xlsx,.xls"
                    beforeUpload={handleImportBackup}
                    showUploadList={false}
                  >
                    <button
                      type="button"
                      className={`sett-form-btn sett-form-btn--secondary${backupLoading ? ' sett-form-btn--loading' : ''}`}
                      disabled={backupLoading}
                    >
                      <UploadOutlined />
                      Загрузить резервную копию
                    </button>
                  </Upload>
                </Form.Item>
              </Form>
            </div>
          </div>

          {/* SIM Manager backup */}
          <div className="sett-info sett-info--spaced-top">
            <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
            <div className="sett-info__body">
              <p className="sett-info__title">Резервное копирование SIM-менеджера</p>
              <p className="sett-info__desc">
                Экспорт и импорт всех SIM-карт в Excel (тот же формат, что на странице SIM-менеджера):
                статусы, модемы, платежи, личные кабинеты, комментарии и скрытые поля «Сервер/Модем опция».
                При авто-бэкапе на SSH Excel выгружается ежедневно вместе с экспортом прокси.
              </p>
            </div>
          </div>

          <div className="sett-card">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Экспорт SIM-менеджера</h3>
            </div>
            <div className="sett-card__body">
              <button
                type="button"
                className={`sett-form-btn sett-form-btn--primary${simBackupLoading ? ' sett-form-btn--loading' : ''}`}
                onClick={handleExportSimBackup}
                disabled={simBackupLoading}
              >
                <DownloadOutlined />
                Выгрузить SIM-менеджер
              </button>
            </div>
          </div>

          <div className="sett-card">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Импорт SIM-менеджера</h3>
            </div>
            <div className="sett-card__body">
              <p className="sett-backup-export-hint" style={{ marginBottom: 12 }}>
                Загрузите Excel-файл из «Экспорт SIM-менеджера» или со страницы SIM-менеджера. Существующие SIM обновляются по номеру телефона.
              </p>
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={handleImportSimBackup}
                showUploadList={false}
              >
                <button
                  type="button"
                  className={`sett-form-btn sett-form-btn--secondary${simBackupLoading ? ' sett-form-btn--loading' : ''}`}
                  disabled={simBackupLoading}
                >
                  <UploadOutlined />
                  Загрузить SIM-менеджер
                </button>
              </Upload>
            </div>
          </div>

          {/* Auto-backups card */}
          <div className="sett-card sett-card--backup-table">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Автоматические резервные копии</h3>
            </div>
            <div className="sett-card__body" style={{ padding: 0 }}>
              <div className="sett-backup-table">
                <Table
                  dataSource={autoBackups}
                  rowKey="filename"
                  pagination={{
                    current: autoBackupPage,
                    pageSize: autoBackupPerPage,
                    showSizeChanger: false,
                    className: 'sett-pagination-hidden',
                  }}
                  onChange={(pag) => {
                    if (!pag) return;
                    setAutoBackupPage(pag.current);
                    setAutoBackupPerPage(pag.pageSize);
                  }}
                  scroll={{ x: 720 }}
                  columns={[
                    {
                      title: 'Имя файла',
                      dataIndex: 'filename',
                      key: 'filename',
                      width: 288,
                      render: (name) => (
                        <span className="sett-backup-filename" title={String(name || '')}>
                          {name}
                        </span>
                      ),
                    },
                    {
                      title: 'Размер',
                      dataIndex: 'size',
                      key: 'size',
                      width: 120,
                      render: (size) => <span className="sett-cell--muted">{(size / 1024).toFixed(2)} KB</span>,
                    },
                    {
                      title: 'Дата создания',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      width: 192,
                      render: (date) => <span className="sett-cell--muted">{formatDate(date)}</span>,
                    },
                    {
                      title: '',
                      key: 'actions',
                      width: 120,
                      render: (_, record) => (
                        <button
                          type="button"
                          className="sett-download-btn"
                          onClick={() => handleDownloadAutoBackup(record.filename)}
                        >
                          <DownloadOutlined />
                          Скачать
                        </button>
                      ),
                    },
                  ]}
                />
                <CustomPagination
                  currentPage={autoBackupPage}
                  totalPages={Math.max(1, Math.ceil((autoBackups?.length || 0) / autoBackupPerPage))}
                  totalItems={autoBackups?.length || 0}
                  perPage={autoBackupPerPage}
                  perPageOptions={[5, 10, 20, 50]}
                  onPageChange={(p) => setAutoBackupPage(p)}
                  onPerPageChange={(v) => {
                    setAutoBackupPerPage(v);
                    setAutoBackupPage(1);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Full system backup card */}
          <div className="sett-card sett-card--backup-table sett-card--full-backup">
            <div className="sett-card__header sett-card__header--row">
              <h3 className="sett-card__title">Полный бэкап системы</h3>
              <div className="sett-card__header-actions">
                <button
                  type="button"
                  className="sett-form-btn sett-form-btn--outline"
                  onClick={handleOpenSSHConfigModal}
                  tabIndex={0}
                  aria-label="Подключить сервер SSH"
                >
                  {sshConfig ? (
                    <>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: sshConfig.is_active ? 'var(--pm-success)' : 'var(--pm-error)', display: 'inline-block' }} />
                      {sshConfig.ssh_host}
                    </>
                  ) : (
                    <>
                      <PlusIcon />
                      Подключить сервер SSH
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className={`sett-form-btn sett-form-btn--primary${fullBackupLoading ? ' sett-form-btn--loading' : ''}`}
                  onClick={handleCreateFullBackup}
                  disabled={fullBackupLoading}
                >
                  <DownloadOutlined />
                  Создать бэкап
                </button>
                <Upload
                  accept=".tar.gz"
                  beforeUpload={handleSelectFullImportFile}
                  showUploadList={false}
                  maxCount={1}
                >
                  <button
                    type="button"
                    className="sett-form-btn sett-form-btn--outline"
                    tabIndex={0}
                    aria-label="Выбрать архив полного бэкапа"
                    disabled={fullImportLoading}
                  >
                    <UploadOutlined />
                    Выбрать архив
                  </button>
                </Upload>
                {fullImportFile && (
                  <span className="sett-full-import-bar__filename" title={fullImportFile.name}>
                    {fullImportFile.name}
                    <button
                      type="button"
                      className="sett-full-import-bar__clear"
                      onClick={handleClearFullImportFile}
                      aria-label="Очистить выбранный файл"
                      tabIndex={0}
                    >
                      ×
                    </button>
                  </span>
                )}
                <Checkbox
                  className="sett-full-import-checkbox"
                  checked={fullImportRestoreSystemConfigs}
                  onChange={(e) => setFullImportRestoreSystemConfigs(e.target.checked)}
                >
                  Восстановить systemd/nginx
                </Checkbox>
                <button
                  type="button"
                  className={`sett-form-btn sett-form-btn--secondary${fullImportLoading ? ' sett-form-btn--loading' : ''}`}
                  onClick={handleImportFullBackup}
                  disabled={fullImportLoading || !fullImportFile}
                  tabIndex={0}
                  aria-label="Импортировать полный бэкап"
                >
                  <UploadOutlined />
                  {fullImportLoading ? 'Обработка...' : 'Импортировать бэкап'}
                </button>
              </div>
            </div>

            {/* SSH config status banner */}
            {sshConfig && (
              <div className="sett-ssh-status-banner">
                <div className="sett-ssh-status-banner__left">
                  <span className={`sett-ssh-status-dot sett-ssh-status-dot--${sshConfig.is_active ? 'online' : 'offline'}`} />
                  <span className="sett-ssh-status-banner__text">
                    SSH: <strong>{sshConfig.ssh_username}@{sshConfig.ssh_host}:{sshConfig.ssh_port}</strong>
                    {' → '}{sshConfig.remote_path}
                  </span>
                  {sshConfig.auto_backup_enabled && (
                    <span className="sett-ssh-auto-badge">
                      Авто каждые {sshConfig.auto_backup_interval_days} дн., после {sshConfig.auto_backup_time || '03:00'}
                    </span>
                  )}
                  {sshConfig.last_auto_backup && (
                    <span className="sett-ssh-status-banner__muted">
                      Последний: {formatDate(sshConfig.last_auto_backup)}
                    </span>
                  )}
                </div>
                <div className="sett-ssh-status-banner__right">
                  <button
                    type="button"
                    className="sett-download-btn"
                    onClick={handleTestSSHConnection}
                    disabled={sshTestLoading}
                    tabIndex={0}
                    aria-label="Проверить подключение"
                  >
                    <RefreshIcon /> {sshTestLoading ? '...' : 'Тест'}
                  </button>
                  <button
                    type="button"
                    className="sett-download-btn"
                    onClick={handleOpenSSHConfigModal}
                    tabIndex={0}
                    aria-label="Настроить"
                  >
                    <EditOutlined /> Настроить
                  </button>
                  <Popconfirm
                    title="Отключить SSH-сервер?"
                    onConfirm={handleDeleteSSHConfig}
                    okText="Да"
                    cancelText="Нет"
                  >
                    <button
                      type="button"
                      className="sett-download-btn"
                      style={{ color: 'var(--pm-error)' }}
                      tabIndex={0}
                      aria-label="Удалить конфигурацию"
                    >
                      <DeleteOutlined />
                    </button>
                  </Popconfirm>
                </div>
              </div>
            )}

            <div className="sett-card__body" style={{ padding: 0 }}>
              <p style={{ padding: '12px 16px 0', color: 'var(--pm-text-muted)', fontSize: 'var(--pm-font-size-xs)', margin: 0 }}>
                Полный архив: дамп БД (pg_dump, все таблицы), роли (pg_dumpall --globals-only), backup_manifest.json, снимок exports/ (прокси xlsx + SIM xlsx), манифесты (uname, df, pip freeze, telegram deps, dpkg), весь проект (.env, Telegram/, migrations, backups/auto) без .git/node_modules/venv; unit-файлы systemd + шаблоны config/; nginx.conf, sites-enabled/available, conf.d.
                {sshConfig?.auto_backup_enabled &&
                  ` Полный бэкап: каждые ${sshConfig.auto_backup_interval_days} дн. (локально и на SSH до 7 шт.). Экспорт прокси и SIM: 1 раз в сутки (на SSH по 7 шт.). Время: не раньше ${sshConfig.auto_backup_time || '03:00'} (сервер).`}
              </p>
              <div className="sett-backup-table">
                <Table
                  dataSource={fullBackups}
                  rowKey="filename"
                  pagination={{
                    current: fullBackupPage,
                    pageSize: fullBackupPerPage,
                    showSizeChanger: false,
                    className: 'sett-pagination-hidden',
                  }}
                  onChange={(pag) => {
                    if (!pag) return;
                    setFullBackupPage(pag.current);
                    setFullBackupPerPage(pag.pageSize);
                  }}
                  scroll={{ x: 960 }}
                  locale={{ emptyText: 'Нет полных бэкапов' }}
                  columns={[
                    {
                      title: 'Имя файла',
                      dataIndex: 'filename',
                      key: 'filename',
                      width: 280,
                      render: (name) => (
                        <span className="sett-backup-filename" title={String(name || '')}>
                          {name}
                        </span>
                      ),
                    },
                    {
                      title: 'Размер',
                      dataIndex: 'size',
                      key: 'size',
                      width: 120,
                      render: (size) => <span className="sett-cell--muted">{(size / 1024 / 1024).toFixed(1)} MB</span>,
                    },
                    {
                      title: 'Дата',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      width: 192,
                      render: (date) => <span className="sett-cell--muted">{formatDate(date)}</span>,
                    },
                    {
                      title: '',
                      key: 'actions',
                      width: 420,
                      render: (_, record) => {
                        const isSshOnlyBackup = record.source === 'ssh';
                        const downloadLabel = isSshOnlyBackup ? 'Скачать SSH' : 'Скачать';
                        const downloadSource = isSshOnlyBackup ? 'ssh' : 'auto';

                        return (
                        <Space size={[4, 8]} wrap className="sett-backup-actions">
                          <button
                            type="button"
                            className="sett-download-btn"
                            onClick={() => handleRestoreListedFullBackup(record)}
                            disabled={fullImportLoading}
                            aria-label="Восстановить бэкап"
                            tabIndex={0}
                          >
                            <HistoryOutlined /> Восстановить
                          </button>
                          <button
                            type="button"
                            className="sett-download-btn"
                            onClick={() => handleDownloadFullBackup(record.filename, downloadSource)}
                            aria-label={downloadLabel}
                            tabIndex={0}
                          >
                            <DownloadOutlined /> {downloadLabel}
                          </button>
                          {sshConfig && (
                            <button
                              type="button"
                              className="sett-download-btn"
                              onClick={() => handleTransferToConnected(record.filename)}
                              aria-label="Отправить на сервер"
                              tabIndex={0}
                            >
                              <UploadOutlined /> На сервер
                            </button>
                          )}
                          <button
                            type="button"
                            className="sett-download-btn"
                            onClick={() => handleOpenSSHTransfer(record.filename)}
                            aria-label="Передать по SSH"
                            tabIndex={0}
                          >
                            <UploadOutlined /> SSH
                          </button>
                          <Popconfirm
                            title="Удалить этот бэкап?"
                            onConfirm={() => handleDeleteFullBackup(record.filename)}
                            okText="Да"
                            cancelText="Нет"
                          >
                            <button
                              type="button"
                              className="sett-download-btn"
                              style={{ color: 'var(--pm-error)' }}
                              aria-label="Удалить"
                              tabIndex={0}
                            >
                              <DeleteOutlined />
                            </button>
                          </Popconfirm>
                        </Space>
                        );
                      },
                    },
                  ]}
                />
                <CustomPagination
                  currentPage={fullBackupPage}
                  totalPages={Math.max(1, Math.ceil((fullBackups?.length || 0) / fullBackupPerPage))}
                  totalItems={fullBackups?.length || 0}
                  perPage={fullBackupPerPage}
                  perPageOptions={[5, 10, 20, 50]}
                  onPageChange={(p) => setFullBackupPage(p)}
                  onPerPageChange={(v) => {
                    setFullBackupPerPage(v);
                    setFullBackupPage(1);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SSH Transfer Modal */}
      <FullImportProgressModal
        open={Boolean(fullImportProgress?.open)}
        title={fullImportProgress?.title || 'Обработка'}
        subtitle={fullImportProgress?.subtitle}
        elapsedLabel={fullImportProgress?.elapsedLabel}
        phases={fullImportProgress?.phases || []}
        percent={fullImportProgress?.percent || 0}
      />
      <FullImportProgressModal
        open={Boolean(proxyImportProgress?.open)}
        title={proxyImportProgress?.title || 'Импорт резервной копии'}
        subtitle={proxyImportProgress?.subtitle}
        elapsedLabel={proxyImportProgress?.elapsedLabel}
        phases={proxyImportProgress?.phases || []}
        percent={proxyImportProgress?.percent || 0}
      />
      <Modal
        title="Передать бэкап на удалённый сервер"
        open={sshModalVisible}
        onCancel={() => setSshModalVisible(false)}
        maskClosable={false}
        wrapProps={sshModalBackdrop}
        footer={null}
        width={480}
      >
        <p style={{ marginBottom: 12, color: 'var(--pm-text-muted)', fontSize: 'var(--pm-font-size-xs)' }}>
          Файл: <strong>{sshTransferFile}</strong>
        </p>
        <Form
          form={sshForm}
          layout="vertical"
          onFinish={handleSSHTransfer}
          initialValues={{ ssh_port: 22, ssh_username: 'root', remote_path: '/root/backups/' }}
        >
          <Form.Item name="ssh_host" label="SSH Host" rules={[{ required: true, message: 'Укажите хост' }]}>
            <Input placeholder="192.168.1.100" />
          </Form.Item>
          <Form.Item name="ssh_port" label="SSH Port">
            <InputNumber min={1} max={65535} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ssh_username" label="Пользователь">
            <Input placeholder="root" />
          </Form.Item>
          <Form.Item name="ssh_password" label="Пароль" rules={[{ required: true, message: 'Укажите пароль' }]}>
            <Input.Password placeholder="Пароль SSH" />
          </Form.Item>
          <Form.Item name="remote_path" label="Путь на сервере">
            <Input placeholder="/root/backups/" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={sshTransferLoading}>
                Передать
              </Button>
              <Button onClick={() => setSshModalVisible(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* SSH Config Modal */}
      <Modal
        title={sshConfig ? 'Настройки SSH-сервера для бэкапов' : 'Подключить SSH-сервер'}
        open={sshConfigModalVisible}
        onCancel={() => setSshConfigModalVisible(false)}
        maskClosable={false}
        wrapProps={sshConfigModalBackdrop}
        footer={null}
        width={560}
      >
        <Form
          form={sshConfigForm}
          layout="vertical"
          onFinish={handleSaveSSHConfig}
          initialValues={{
            ssh_port: 22,
            ssh_username: 'root',
            remote_path: '/root/backups/',
            auto_backup_enabled: true,
            auto_backup_interval_days: 2,
            auto_backup_time: dayjs('03:00', 'HH:mm'),
          }}
        >
          <div className="sett-form-grid">
            <Form.Item name="ssh_host" label="SSH Host" rules={[{ required: true, message: 'Укажите хост' }]}>
              <Input placeholder="192.168.1.100" />
            </Form.Item>
            <Form.Item name="ssh_port" label="SSH Port">
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <div className="sett-form-grid">
            <Form.Item name="ssh_username" label="Пользователь">
              <Input placeholder="root" />
            </Form.Item>
            <Form.Item name="remote_path" label="Путь на сервере">
              <Input placeholder="/root/backups/" />
            </Form.Item>
          </div>

          <div className="sett-section-divider">Аутентификация</div>
          <Form.Item
            name="ssh_password"
            label="Пароль SSH"
            extra="При первом подключении укажите пароль — ключ сгенерируется, добавится на сервер и будет создан каталог бэкапов. Дальше поле можно оставлять пустым."
          >
            <Input.Password placeholder="Пароль SSH" />
          </Form.Item>
          <Form.Item
            name="ssh_private_key"
            label="Приватный SSH-ключ (PEM)"
            extra="Необязательно: если пусто, при сохранении создаётся новая пара ключей автоматически."
          >
            <Input.TextArea rows={4} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----&#10;...&#10;-----END OPENSSH PRIVATE KEY-----" />
          </Form.Item>

          {sshConfig?.ssh_public_key && (
            <div className="sett-ssh-pubkey-block">
              <label className="sett-ssh-pubkey-label">Публичный SSH-ключ (уже на сервере при сохранении с паролем; можно скопировать вручную)</label>
              <Input.TextArea
                value={sshConfig.ssh_public_key}
                readOnly
                rows={3}
                onClick={(e) => {
                  e.target.select();
                  navigator.clipboard.writeText(sshConfig.ssh_public_key).then(() => {
                    message.success('Ключ скопирован');
                  });
                }}
                style={{ cursor: 'pointer', fontSize: 11, fontFamily: 'monospace' }}
              />
              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  onClick={handleInstallSSHKey}
                  loading={sshKeyInstallLoading}
                  disabled={!sshConfig?.has_password}
                  title={!sshConfig?.has_password ? 'Для установки ключа нужен сохранённый пароль' : 'Установить ключ на сервер автоматически'}
                >
                  Установить на сервер
                </Button>
                <Button
                  size="small"
                  onClick={handleGenerateSSHKey}
                  loading={sshKeyGenLoading}
                >
                  Перегенерировать ключ
                </Button>
              </div>
            </div>
          )}

          {sshConfig && !sshConfig?.ssh_public_key && (
            <Button
              size="small"
              style={{ marginBottom: 16 }}
              onClick={() => handleGenerateSSHKey()}
              loading={sshKeyGenLoading}
            >
              Сгенерировать SSH-ключ
            </Button>
          )}

          <div className="sett-section-divider">Автоматический бэкап</div>
          <p className="sett-ssh-schedule-hint" style={{ marginBottom: 12, fontSize: 13, opacity: 0.85 }}>
            Планировщик на сервере проверяет условия каждые 30 мин (локальное время машины с бэкендом).
            Бэкап запускается, если прошло не меньше указанного числа дней с последнего запуска и текущее время не раньше выбранного.
          </p>
          <div className="sett-form-grid">
            <Form.Item name="auto_backup_enabled" valuePropName="checked" label="Включить авто-бэкап">
              <Switch />
            </Form.Item>
            <Form.Item name="auto_backup_interval_days" label="Интервал (дни)">
              <InputNumber min={1} max={30} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Form.Item
            name="auto_backup_time"
            label="Время запуска (локальное время сервера)"
            extra="Не раньше этого времени суток начнётся бэкап после того, как истечёт интервал в днях."
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={5} needConfirm={false} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={sshConfigLoading}>
                {sshConfig ? 'Сохранить' : 'Подключить'}
              </Button>
              <Button onClick={() => setSshConfigModalVisible(false)}>Отмена</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {activeTab === 'modem-defaults' && (
        <div className="sett-tab-content">
          {/* Info banner */}
          <div className="sett-info">
            <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
            <div className="sett-info__body">
              <p className="sett-info__title">Настройки модемов по умолчанию</p>
              <p className="sett-info__desc">Значения применяются к модемам, с которых выполнена автозамена или переподключение, если на модеме не осталось прокси с настройками.</p>
            </div>
          </div>

          {/* Settings card */}
          <div className="sett-card">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Настройки по умолчанию</h3>
            </div>
            <div className="sett-card__body">
              <Form
                form={modemDefaultsForm}
                layout="vertical"
                onFinish={handleSaveModemDefaults}
              >
                {/* Мониторинг */}
                <div className="sett-section-divider">Настройки мониторинга</div>
                <div className="sett-form-grid sett-form-grid--single">
                  <Form.Item
                    name="monitoring_interval"
                    label="Интервал мониторинга (сек)"
                    rules={[{ required: true, message: 'Укажите интервал мониторинга' }]}
                  >
                    <InputNumber min={1} max={86400} style={{ width: '100%' }} />
                  </Form.Item>
                </div>

                {/* Переподключение */}
                <div className="sett-section-divider">Настройки переподключения</div>
                <div className="sett-form-grid">
                  <Form.Item
                    name="reconnect_type"
                    label="Тип переподключения"
                    rules={[{ required: true, message: 'Выберите тип переподключения' }]}
                  >
                    <Select placeholder="Выберите тип">
                      <Select.Option value="time">По времени</Select.Option>
                      <Select.Option value="link">Ссылка</Select.Option>
                      <Select.Option value="time_link">Время + ссылка</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="reconnect_interval"
                    label="Интервал переподключения (сек)"
                    rules={[{ required: true, message: 'Укажите интервал переподключения' }]}
                  >
                    <InputNumber min={1} max={86400} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="reconnect_min"
                    label="Минимальный шаг (сек)"
                    rules={[{ required: true, message: 'Укажите минимальный шаг' }]}
                  >
                    <InputNumber min={1} max={86400} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="mtu"
                    label="MTU (0 - по умолчанию)"
                    rules={[{ required: true, message: 'Укажите MTU' }]}
                  >
                    <InputNumber min={0} max={9000} style={{ width: '100%' }} />
                  </Form.Item>
                </div>

                {/* TCP отпечаток */}
                <div className="sett-section-divider">TCP отпечаток</div>
                <div className="sett-form-grid">
                  <Form.Item
                    name="is_osfp"
                    label="Использовать подмену TCP отпечатка"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item shouldUpdate={(prev, current) => prev.is_osfp !== current.is_osfp}>
                    {({ getFieldValue }) => {
                      const isOsfpEnabled = !!getFieldValue('is_osfp');
                      return (
                        <Form.Item
                          name="osfp_id"
                          label="TCP отпечаток"
                        >
                          <Select
                            placeholder="Выберите отпечаток"
                            allowClear
                            disabled={!isOsfpEnabled}
                          >
                            {osfpList.map((osfp) => (
                              <Select.Option key={osfp.id} value={osfp.id}>
                                {osfp.display_name || osfp.name || `ID: ${osfp.id}`}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </div>

                {/* Actions */}
                <div className="sett-form-actions">
                  <button
                    type="submit"
                    className={`sett-form-btn sett-form-btn--primary${modemDefaultsLoading ? ' sett-form-btn--loading' : ''}`}
                    disabled={modemDefaultsLoading}
                  >
                    Сохранить
                  </button>
                  <button
                    type="button"
                    className={`sett-form-btn sett-form-btn--secondary${modemDefaultsLoading ? ' sett-form-btn--loading' : ''}`}
                    onClick={handleLoadModemDefaults}
                    disabled={modemDefaultsLoading}
                  >
                    Обновить
                  </button>
                </div>
              </Form>
            </div>
          </div>

          <div className="sett-info sett-info--spaced-top">
            <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
            <div className="sett-info__body">
              <p className="sett-info__title">Плановое пересохранение настроек</p>
              <p className="sett-info__desc">
                Для каждого активного модема повторно отправляет в Kraken те же параметры (мониторинг, переподключение, MTU, OSFP), что сейчас записаны в БД по этому модему — без фильтра по прокси. Время запуска — МСК.
              </p>
            </div>
          </div>

          <div className="sett-card">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Расписание пересохранения</h3>
            </div>
            <div className="sett-card__body">
              <Form
                form={modemResyncForm}
                layout="vertical"
                onFinish={handleSaveModemResync}
                initialValues={{ enabled: false, run_time_hhmm: '03:00' }}
              >
                <div className="sett-form-grid sett-form-grid--single">
                  <Form.Item
                    name="enabled"
                    label="Включить ежедневный запуск"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    name="run_time_hhmm"
                    label="Время запуска"
                    rules={[
                      { required: true, message: 'Укажите время' },
                      {
                        validator: (_, value) => {
                          if (value == null || String(value).trim() === '') {
                            return Promise.reject(new Error('Укажите время'));
                          }
                          if (parseRunTimeHhmm(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error('Формат: 23:15, 23.15 или 23,15')
                          );
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="23:15 или 23.15"
                      inputMode="text"
                      autoComplete="off"
                      onBlur={handleModemResyncTimeBlur}
                      aria-label="Время ежедневного запуска пересохранения модемов"
                    />
                  </Form.Item>
                </div>
                {(modemResyncMeta.last_run_at || modemResyncMeta.last_run_summary) && (
                  <div className="sett-section-divider">Последний запуск</div>
                )}
                {modemResyncMeta.last_run_at && (
                  <p className="sett-info__desc" style={{ marginBottom: 8 }}>
                    {formatMskDateTime(modemResyncMeta.last_run_at)}
                  </p>
                )}
                {modemResyncMeta.last_run_summary && typeof modemResyncMeta.last_run_summary === 'object' && (
                  <p className="sett-info__desc">
                    Модемов (попыток): {modemResyncMeta.last_run_summary.processed ?? '—'}
                    {', '}
                    успешно к Kraken: {modemResyncMeta.last_run_summary.applied_kraken ?? '—'}
                    {', '}
                    ошибок: {modemResyncMeta.last_run_summary.failed ?? '—'}
                    {modemResyncMeta.last_run_summary.skipped_no_connection != null && modemResyncMeta.last_run_summary.skipped_no_connection > 0
                      ? `, без подключения: ${modemResyncMeta.last_run_summary.skipped_no_connection}`
                      : ''}
                  </p>
                )}
                <div className="sett-form-actions">
                  <button
                    type="submit"
                    className={`sett-form-btn sett-form-btn--primary${modemResyncLoading ? ' sett-form-btn--loading' : ''}`}
                    disabled={modemResyncLoading}
                  >
                    Сохранить
                  </button>
                  <button
                    type="button"
                    className={`sett-form-btn sett-form-btn--secondary${modemResyncLoading ? ' sett-form-btn--loading' : ''}`}
                    onClick={handleLoadModemResync}
                    disabled={modemResyncLoading}
                  >
                    Обновить
                  </button>
                  <button
                    type="button"
                    className={`sett-form-btn sett-form-btn--secondary${modemResyncRunLoading ? ' sett-form-btn--loading' : ''}`}
                    onClick={handleRunModemResyncNow}
                    disabled={modemResyncRunLoading || modemResyncLoading}
                    tabIndex={0}
                    aria-label="Запустить пересохранение настроек модемов сейчас"
                  >
                    Запустить сейчас
                  </button>
                </div>
              </Form>
            </div>
          </div>

        </div>
      )}

      {isLogsSection && activeTab === 'logs' && (
        <div className="sett-content">
          {/* Filters */}
          <div className="sett-filters">
            <CustomSelect
              className="cs--filter"
              value={logsFilters.category}
              onChange={(val) => handleLogsFilterChange('category', val)}
              ariaLabel="Фильтр по категории"
              placeholder="Все категории"
              options={[
                { value: '', label: 'Все категории' },
                ...Object.entries(CATEGORY_LABELS).map(([k, v]) => ({ value: k, label: v })),
              ]}
            />

            <CustomSelect
              className="cs--filter"
              value={logsFilters.level}
              onChange={(val) => handleLogsFilterChange('level', val)}
              ariaLabel="Фильтр по уровню"
              placeholder="Все уровни"
              options={[
                { value: '', label: 'Все уровни' },
                { value: 'info', label: 'Info' },
                { value: 'warning', label: 'Warning' },
                { value: 'error', label: 'Error' },
              ]}
            />

            <input
              className="sett-filter-input"
              type="text"
              placeholder="Поиск по сообщению…"
              value={logsFilters.search}
              onChange={(e) => handleLogsFilterChange('search', e.target.value)}
              aria-label="Поиск по сообщению"
            />

            <input
              className="sett-filter-input"
              type="text"
              placeholder="Клиент…"
              value={logsFilters.log_client}
              onChange={(e) => handleLogsFilterChange('log_client', e.target.value)}
              aria-label="Фильтр по клиенту"
            />
            <input
              className="sett-filter-input"
              type="text"
              placeholder="Подключение…"
              value={logsFilters.log_server}
              onChange={(e) => handleLogsFilterChange('log_server', e.target.value)}
              aria-label="Фильтр по подключению Kraken"
            />
            <input
              className="sett-filter-input"
              type="text"
              placeholder="Номер модема…"
              value={logsFilters.log_modem}
              onChange={(e) => handleLogsFilterChange('log_modem', e.target.value)}
              aria-label="Фильтр по номеру модема"
            />
            <input
              className="sett-filter-input"
              type="text"
              placeholder="Порт…"
              value={logsFilters.log_port}
              onChange={(e) => handleLogsFilterChange('log_port', e.target.value)}
              aria-label="Фильтр по порту"
            />

            <div className="sett-filter-date-row">
              <label className="sett-filter-date-wrap">
                <span className="sett-filter-date-label">Дата от</span>
                <input
                  className="sett-filter-date"
                  type="date"
                  value={logsFilters.date_from}
                  onClick={(e) => { try { e.target.showPicker(); } catch (_) { /* fallback */ } }}
                  onChange={(e) => handleLogsFilterChange('date_from', e.target.value)}
                  aria-label="Дата от"
                  title="Дата от"
                />
              </label>
              <label className="sett-filter-date-wrap">
                <span className="sett-filter-date-label">Дата до</span>
                <input
                  className="sett-filter-date"
                  type="date"
                  value={logsFilters.date_to}
                  onClick={(e) => { try { e.target.showPicker(); } catch (_) { /* fallback */ } }}
                  onChange={(e) => handleLogsFilterChange('date_to', e.target.value)}
                  aria-label="Дата до"
                  title="Дата до"
                />
              </label>
            </div>

            <div className="sett-filters__actions">
              <button
                type="button"
                className="sett-form-btn sett-form-btn--secondary"
                tabIndex={0}
                aria-label="Только логи модемов"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => handleApplyModemLogsPreset(false)}
              >
                Только модемы
              </button>
              <button
                type="button"
                className="sett-form-btn sett-form-btn--secondary"
                tabIndex={0}
                aria-label="Только ошибки модемов"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => handleApplyModemLogsPreset(true)}
              >
                Ошибки модемов
              </button>
              <button
                type="button"
                className="sett-form-btn sett-form-btn--secondary"
                tabIndex={0}
                aria-label="Только рассинхроны"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => handleApplyMismatchLogsPreset(false)}
              >
                Рассинхроны
              </button>
              <button
                type="button"
                className="sett-form-btn sett-form-btn--secondary"
                tabIndex={0}
                aria-label="Ошибки рассинхронов"
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => handleApplyMismatchLogsPreset(true)}
              >
                Ошибки рассинхронов
              </button>
              <Popconfirm
                title="Удалить логи старше 30 дней?"
                onConfirm={handleClearOldLogs}
                okText="Да"
                cancelText="Нет"
              >
                <button
                  type="button"
                  className="sett-form-btn sett-form-btn--secondary"
                  tabIndex={0}
                  aria-label="Очистить старые логи"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Очистить старые
                </button>
              </Popconfirm>
            </div>
          </div>

          {/* Logs table */}
          <div className="sett-table">
            <Table
              dataSource={systemLogs}
              columns={logsColumns}
              rowKey="id"
              loading={logsLoading}
              size="small"
              expandable={{
                expandedRowRender: (record) => {
                  if (!record.details) return <span style={{ color: 'var(--pm-text-muted)' }}>Нет деталей</span>;
                  const details = typeof record.details === 'string' ? JSON.parse(record.details) : record.details;
                  return (
                    <pre style={{
                      margin: 0,
                      padding: '8px 12px',
                      fontSize: 12,
                      background: 'var(--pm-bg-secondary, #f5f5f5)',
                      borderRadius: 6,
                      maxHeight: 300,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                      {JSON.stringify(details, null, 2)}
                    </pre>
                  );
                },
                rowExpandable: (record) => !!record.details,
              }}
              pagination={false}
              scroll={{ x: 1280 }}
            />
            <CustomPagination
              currentPage={logsPage}
              totalPages={Math.max(1, Math.ceil(logsTotal / LOGS_PAGE_SIZE))}
              totalItems={logsTotal}
              perPage={LOGS_PAGE_SIZE}
              perPageOptions={[50, 100]}
              onPageChange={(p) => { setLogsPage(p); fetchSystemLogs(p); }}
              onPerPageChange={() => {}}
            />
          </div>
        </div>
      )}

      {isLogsSection && activeTab === 'proxy-replacement-logs' && (
        <div className="sett-content sett-content--proxy-checker-embed">
          <ProxyChecker
            embedded
            embeddedActiveTab="detailed-replacement-logs"
          />
        </div>
      )}

      {isLogsSection && activeTab === 'modem-reboot-logs' && (
        <div className="sett-content sett-content--proxy-checker-embed">
          <ProxyChecker
            embedded
            embeddedActiveTab="modem-reboot-logs"
          />
        </div>
      )}

      {isLogsSection && activeTab === 'balance-operations-logs' && (
        <BalanceOperationsLogs refreshNonce={balanceOpsRefreshNonce} />
      )}

      {!isLogsSection && activeTab === 'proxy-check-rules' && (
        <div className="sett-content sett-content--proxy-checker-embed">
          <ProxyChecker
            embedded
            embeddedActiveTab="rules"
          />
        </div>
      )}

      {activeTab === 'operators' && (
        <div className="sett-tab-content">
          <div className="sett-info">
            <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
            <div className="sett-info__body">
              <p className="sett-info__title">Шаблоны операторов</p>
              <p className="sett-info__desc">
                Задайте канонические названия и wildcard-маски (`*`) для унификации операторов во всех формах и таблицах.
                Для SIM-менеджера укажите ссылку на личный кабинет: плейсхолдер <code>{'{phone}'}</code> — номер из привязанного ЛК, <code>{'{phone_number}'}</code> — номер SIM из таблицы. Если плейсхолдер указан, а значения нет, кнопка «ЛК» неактивна.
              </p>
            </div>
          </div>

          <div className="sett-card">
            <div className="sett-card__header sett-card__header--row">
              <h3 className="sett-card__title">Шаблоны операторов</h3>
              <div className="sett-card__header-actions">
                <Space wrap>
                  <Button onClick={handleAddOperatorTemplate}>Добавить</Button>
                  <Button loading={operatorsLoading} onClick={handleLoadOperatorTemplates}>Обновить</Button>
                  <Button type="primary" loading={operatorsLoading} onClick={handleSaveOperatorTemplates}>Сохранить</Button>
                </Space>
              </div>
            </div>
            <div className="sett-card__body">
              {(operatorTemplates || []).map((item, index) => (
                <div
                  key={`op-tpl-${String(item?.name || '').trim() || 'new'}-${index}`}
                  className="sett-operator-row"
                >
                  <Input
                    placeholder="Каноническое имя"
                    value={item.name}
                    onChange={(e) => handleOperatorTemplateNameChange(index, e.target.value)}
                    aria-label={`Оператор ${index + 1}: каноническое имя`}
                  />
                  <Input
                    placeholder="*mega*; *fon*; *dmo*"
                    value={item.patternsText ?? (item.patterns || []).join('; ')}
                    onChange={(e) => handleOperatorTemplatePatternsChange(index, e.target.value)}
                    aria-label={`Оператор ${index + 1}: маски`}
                  />
                  <Input
                    placeholder={'https://… · {phone} — ЛК, {phone_number} — номер SIM'}
                    value={item.personal_cabinet_url ?? ''}
                    onChange={(e) => handleOperatorTemplatePersonalCabinetUrlChange(index, e.target.value)}
                    aria-label={`Оператор ${index + 1}: ссылка личного кабинета`}
                  />
                  <Button danger onClick={() => handleDeleteOperatorTemplate(index)}>Удалить</Button>
                </div>
              ))}
              {operatorTemplates.length === 0 && (
                <div className="sett-cell--muted">Шаблоны не добавлены</div>
              )}
            </div>
          </div>

          <div className="sett-card">
            <div className="sett-card__header">
              <h3 className="sett-card__title">Превью матчинга</h3>
            </div>
            <div className="sett-card__body">
              <Input.TextArea
                rows={4}
                placeholder={'MegaFon RUS\nmega\nt2 rus'}
                value={operatorsPreviewInput}
                onChange={(e) => setOperatorsPreviewInput(e.target.value)}
              />
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <Button loading={operatorsLoading} onClick={handlePreviewOperatorTemplates}>Проверить</Button>
              </div>
              <div>
                {operatorsPreviewResult.map((item, idx) => (
                  <div key={`preview-${idx}`} style={{ marginBottom: 4 }}>
                    <span className="sett-cell--muted">{item.source}</span> -> <b>{item.canonical}</b>
                  </div>
                ))}
                {operatorsPreviewResult.length === 0 && <div className="sett-cell--muted">Нет данных для превью</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'integrations' && (
        <div className="sett-tab-content">
          <div className="sett-info">
            <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
            <div className="sett-info__body">
              <p className="sett-info__title">Интеграции</p>
              <p className="sett-info__desc">
                Настройки сохраняются в <code>.env</code> на сервере и применяются в текущем процессе backend.
                Секреты отображаются маской; оставьте поле пустым, чтобы не менять уже заданное значение.
                Telegram-бот и фоновые воркеры — после перезапуска.
              </p>
            </div>
          </div>

          {integrationsLoading && !integrationsSettings ? (
            <div className="sett-cell--muted">Загрузка…</div>
          ) : (
            <Form form={integrationsForm} layout="vertical">
              {(integrationsSettings?.sections || []).map((section) => (
                <div key={section.id} className="sett-card">
                  <div className="sett-card__header sett-card__header--row">
                    <h3 className="sett-card__title">{section.title}</h3>
                    <div className="sett-card__header-actions">
                      {section.checkable && (
                        <Button
                          loading={integrationsCheckingSectionId === section.id}
                          disabled={integrationsLoading}
                          onClick={() => handleCheckIntegrationsSection(section)}
                          aria-label={`Проверить ${section.title}`}
                        >
                          Проверить
                        </Button>
                      )}
                      <Button
                        type="primary"
                        loading={integrationsSavingSectionId === section.id}
                        disabled={integrationsLoading}
                        onClick={() => handleSaveIntegrationsSection(section)}
                        aria-label={`Сохранить ${section.title}`}
                      >
                        Сохранить
                      </Button>
                    </div>
                  </div>
                  <div className="sett-card__body">
                    <div className="sett-integration-grid">
                      {(section.fields || []).map((field) => (
                        <Form.Item
                          key={field.key}
                          name={field.key}
                          label={field.label}
                          className="sett-integration-field"
                        >
                          {field.secret ? (
                            <Input.Password
                              placeholder={
                                field.configured
                                  ? `${INTEGRATIONS_SECRET_MASK} — оставьте пустым, чтобы не менять`
                                  : 'Не задано'
                              }
                              className="sett-integration-field__input--secret"
                              aria-label={`${section.title}: ${field.label}`}
                            />
                          ) : (
                            <Input
                              placeholder="—"
                              aria-label={`${section.title}: ${field.label}`}
                            />
                          )}
                        </Form.Item>
                      ))}
                    </div>
                    {section.checkable && (
                      <IntegrationCheckResult
                        title={section.title}
                        result={integrationsChecks[section.id]?.result}
                        error={integrationsChecks[section.id]?.error}
                      />
                    )}
                  </div>
                </div>
              ))}
            </Form>
          )}
        </div>
      )}

      {activeTab === 'lk-defaults' && isClientCabinetSection && (
        <ConfigProvider locale={ruRU}>
        <div className="sett-tab-content">
          <div className="sett-info">
            <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
            <div className="sett-info__body">
              <p className="sett-info__title">Настройки личного кабинета клиента</p>
              <p className="sett-info__desc">
                Стартовый баланс, привилегии, автоудаление просроченных прокси, отпечатки ОС
                и расписание очистки leftover в Kraken.
                Сторонние скрипты — на вкладке «Скрипты», прокси для Telegram-бота — на «Прокси TG»,
                дефолтные запреты ресурсов Kraken при покупке — на «Запрещенные ресурсы».
                Информер и рассылки — в разделе «Маркетинг».
              </p>
            </div>
          </div>

          <Form
            form={cabinetDefaultsForm}
            layout="vertical"
            onValuesChange={(changed, all) => {
              if (cabinetLkApplyingRef.current) return;
              cabinetLkFormFieldsRef.current = {
                default_balance_rub: Number(all.default_balance_rub ?? cabinetLkFormFieldsRef.current.default_balance_rub),
                default_privilege_tier: all.default_privilege_tier || cabinetLkFormFieldsRef.current.default_privilege_tier,
                default_privilege_duration_days: Number(
                  all.default_privilege_duration_days ?? cabinetLkFormFieldsRef.current.default_privilege_duration_days,
                ),
                support_telegram_url:
                  all.support_telegram_url ?? cabinetLkFormFieldsRef.current.support_telegram_url,
                support_max_url: all.support_max_url ?? cabinetLkFormFieldsRef.current.support_max_url,
              };
              if (Object.prototype.hasOwnProperty.call(changed, 'default_balance_rub')) {
                markLkSectionDirty('balance');
              }
              if (
                Object.prototype.hasOwnProperty.call(changed, 'default_privilege_tier')
                || Object.prototype.hasOwnProperty.call(changed, 'default_privilege_duration_days')
              ) {
                markLkSectionDirty('privilege');
              }
              if (
                Object.prototype.hasOwnProperty.call(changed, 'unpaid_proxy_auto_delete_enabled')
                || Object.prototype.hasOwnProperty.call(changed, 'unpaid_proxy_delete_grace_hours')
              ) {
                markLkSectionDirty('autoDelete');
              }
              if (
                Object.prototype.hasOwnProperty.call(changed, 'support_telegram_url')
                || Object.prototype.hasOwnProperty.call(changed, 'support_max_url')
              ) {
                markLkSectionDirty('support');
              }
              if (Object.prototype.hasOwnProperty.call(changed, 'cabinet_pof_visible_ids')) {
                markLkSectionDirty('fingerprints');
              }
              if (
                Object.prototype.hasOwnProperty.call(changed, 'default_kraken_is_allow')
                || Object.prototype.hasOwnProperty.call(changed, 'default_kraken_is_ports')
                || Object.prototype.hasOwnProperty.call(changed, 'default_kraken_allow_input')
                || Object.prototype.hasOwnProperty.call(changed, 'default_kraken_targetport_input')
              ) {
                markLkSectionDirty('forbiddenResources');
              }
            }}
          >
            {clientCabinetTab === 'main' && (
            <>
            <div className="sett-lk-balance-privilege-row">
              <div className="sett-card sett-lk-balance-privilege-row__card">
                <div className="sett-card__header sett-card__header--row">
                  <h3 className="sett-card__title">Баланс для новых клиентов</h3>
                  <Button
                    type={lkSectionDirty.balance ? 'primary' : 'default'}
                    disabled={!lkSectionDirty.balance}
                    loading={lkSectionSaving.balance}
                    onClick={() => handleSaveLkCabinetSection('balance')}
                    aria-label="Сохранить стартовый баланс"
                  >
                    Сохранить
                  </Button>
                </div>
                <div className="sett-card__body">
                  <Form.Item
                    name="default_balance_rub"
                    label="Стартовый баланс, ₽"
                    rules={[{ required: true, message: 'Укажите сумму' }]}
                    className="sett-lk-balance-field"
                  >
                    <InputNumber
                      min={0}
                      step={10}
                      style={{ width: '100%' }}
                      aria-label="Стартовый баланс для новых клиентов"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="sett-card sett-lk-balance-privilege-row__card">
                <div className="sett-card__header sett-card__header--row">
                  <h3 className="sett-card__title">Привилегия для новых клиентов</h3>
                  <Button
                    type={lkSectionDirty.privilege ? 'primary' : 'default'}
                    disabled={!lkSectionDirty.privilege}
                    loading={lkSectionSaving.privilege}
                    onClick={() => handleSaveLkCabinetSection('privilege')}
                    aria-label="Сохранить настройки привилегий"
                  >
                    Сохранить
                  </Button>
                </div>
                <div className="sett-card__body">
                  <div className="sett-lk-privilege-fields">
                    <Form.Item
                      name="default_privilege_tier"
                      label="Уровень привилегий"
                      className="sett-lk-privilege-fields__tier"
                      rules={[{ required: true, message: 'Выберите уровень' }]}
                    >
                      <Select aria-label="Уровень привилегий по умолчанию">
                        <Option value="bronze">Бронза</Option>
                        <Option value="silver">Серебро</Option>
                        <Option value="gold">Золото</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="default_privilege_duration_days"
                      label="Срок действия, дней (0 — отключено)"
                      className="sett-lk-privilege-fields__duration"
                      rules={[{ required: true, message: 'Укажите срок' }]}
                    >
                      <InputNumber
                        min={0}
                        max={3650}
                        step={1}
                        style={{ width: '100%' }}
                        aria-label="Срок действия уровня привилегий"
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>

            <div className="sett-card">
              <div className="sett-card__header sett-card__header--row">
                <h3 className="sett-card__title">Автоудаление</h3>
                <Button
                  type={lkSectionDirty.autoDelete ? 'primary' : 'default'}
                  disabled={!lkSectionDirty.autoDelete}
                  loading={lkSectionSaving.autoDelete}
                  onClick={() => handleSaveLkCabinetSection('autoDelete')}
                  aria-label="Сохранить автоудаление просроченных прокси"
                >
                  Сохранить
                </Button>
              </div>
              <div className="sett-card__body">
                <Form.Item
                  name="unpaid_proxy_auto_delete_enabled"
                  label="Автоудаление просроченных прокси (непродление)"
                  valuePropName="checked"
                >
                  <Switch aria-label="Включить автоудаление просроченных прокси" />
                </Form.Item>
                <Form.Item
                  name="unpaid_proxy_delete_grace_hours"
                  label="Через сколько часов после просрочки expires_at удалять прокси"
                  extra="0 — удалять сразу после просрочки (на следующем проходе планировщика, обычно до 5 минут)."
                  rules={[
                    { required: true, message: 'Укажите число часов' },
                    { type: 'number', min: 0, max: 8760, message: 'От 0 до 8760' },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={8760}
                    step={1}
                    style={{ width: '100%', maxWidth: 280 }}
                    aria-label="Срок в часах до удаления просроченной прокси"
                  />
                </Form.Item>
              </div>
            </div>

            <div className="sett-card">
              <div className="sett-card__header sett-card__header--row">
                <h3 className="sett-card__title">Мессенджеры поддержки</h3>
                <Button
                  type={lkSectionDirty.support ? 'primary' : 'default'}
                  disabled={!lkSectionDirty.support}
                  loading={lkSectionSaving.support}
                  onClick={() => handleSaveLkCabinetSection('support')}
                  aria-label="Сохранить ссылки мессенджеров поддержки"
                >
                  Сохранить
                </Button>
              </div>
              <div className="sett-card__body">
                <Form.Item
                  name="support_telegram_url"
                  label="Ссылка на Telegram в блоке «Поддержка»"
                  rules={[
                    { required: true, message: 'Укажите ссылку Telegram' },
                    { type: 'url', message: 'Укажите корректный URL (https://...)' },
                  ]}
                >
                  <Input
                    placeholder="https://t.me/+79939885347"
                    aria-label="Ссылка Telegram для блока поддержки"
                  />
                </Form.Item>
                <Form.Item
                  name="support_max_url"
                  label="Ссылка на MAX в блоке «Поддержка»"
                  rules={[
                    { required: true, message: 'Укажите ссылку MAX' },
                    { type: 'url', message: 'Укажите корректный URL (https://...)' },
                  ]}
                >
                  <Input
                    placeholder="https://max.ru/u/..."
                    aria-label="Ссылка MAX для блока поддержки"
                  />
                </Form.Item>
              </div>
            </div>

            <div className="sett-card">
              <div className="sett-card__header sett-card__header--row">
                <h3 className="sett-card__title">Отпечатки</h3>
                <Button
                  type={lkSectionDirty.fingerprints ? 'primary' : 'default'}
                  disabled={!lkSectionDirty.fingerprints}
                  loading={lkSectionSaving.fingerprints}
                  onClick={() => handleSaveLkCabinetSection('fingerprints')}
                  aria-label="Сохранить отпечатки ОС"
                >
                  Сохранить
                </Button>
              </div>
              <div className="sett-card__body">
                <Form.Item
                  label="Отпечатки ОС (pOF) в личном кабинете"
                  extra="Список из Kraken. Отмеченные варианты доступны клиенту при покупке и редактировании прокси (тариф «Индивидуальный»)."
                >
                  <div className="sett-pof-catalog">
                    <div className="sett-pof-catalog__toolbar">
                      <Button
                        type="link"
                        size="small"
                        htmlType="button"
                        onClick={handleCabinetPofSelectAll}
                        disabled={!cabinetPofCheckboxOptions.length || cabinetPofCatalogLoading}
                        aria-label="Выбрать все отпечатки ОС"
                      >
                        Выбрать все
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        htmlType="button"
                        onClick={handleCabinetPofClearAll}
                        disabled={cabinetPofCatalogLoading}
                        aria-label="Снять выбор со всех отпечатков ОС"
                      >
                        Снять все
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        htmlType="button"
                        onClick={handleLoadCabinetPofCatalog}
                        loading={cabinetPofCatalogLoading}
                        aria-label="Обновить список отпечатков из Kraken"
                      >
                        Обновить из Kraken
                      </Button>
                    </div>
                    {cabinetPofCatalogLoading && !cabinetPofCheckboxOptions.length ? (
                      <span className="sett-pof-catalog__hint">Загрузка отпечатков из Kraken…</span>
                    ) : null}
                    {!cabinetPofCatalogLoading && !cabinetPofCheckboxOptions.length ? (
                      <span className="sett-pof-catalog__hint">
                        Нет данных. Проверьте активные подключения Kraken и нажмите «Обновить из Kraken».
                      </span>
                    ) : (
                      <Form.Item
                        name="cabinet_pof_visible_ids"
                        noStyle
                        rules={[
                          {
                            validator: (_, value) => {
                              const ids = Array.isArray(value)
                                ? value.filter((id) => Number.isFinite(Number(id)) && Number(id) > 0)
                                : [];
                              if (!ids.length) {
                                return Promise.reject(new Error('Выберите хотя бы один отпечаток'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Checkbox.Group
                          className="sett-pof-catalog__list"
                          options={cabinetPofCheckboxOptions}
                          aria-label="Отпечатки ОС для личного кабинета"
                        />
                      </Form.Item>
                    )}
                  </div>
                </Form.Item>
              </div>
            </div>
            </>
            )}

            {clientCabinetTab === 'scripts' && (
            <div className="sett-card">
              <div className="sett-card__header sett-card__header--row">
                <h3 className="sett-card__title">Сторонние скрипты</h3>
                <div className="sett-card__header-actions">
                  <Button
                    type={lkSectionDirty.scripts ? 'primary' : 'default'}
                    disabled={!lkSectionDirty.scripts}
                    loading={lkSectionSaving.scripts}
                    onClick={() => handleSaveLkCabinetSection('scripts')}
                    aria-label="Сохранить сторонние скрипты"
                  >
                    Сохранить
                  </Button>
                  <Button type="default" onClick={handleAddCabinetScript}>
                    Добавить скрипт
                  </Button>
                </div>
              </div>
              <div className="sett-card__body">
                {cabinetScripts.length === 0 && (
                  <div className="sett-cell--muted">
                    Скрипты не добавлены. Код — в развёрнутой области (стрелка у строки). Можно вставить блок из
                    кабинета Метрики/Jivo целиком: комментарии и noscript отбрасываются, сохраняются только
                    допустимые теги {'<script>…</script>'} (один или несколько подряд).
                  </div>
                )}
                {cabinetScripts.map((item, index) => {
                  const scriptRowKey = item.id || `script-${index}`;
                  const isScriptBodyExpanded = cabinetScriptBodyExpanded[scriptRowKey] === true;
                  const handleToggleScriptBody = () => {
                    setCabinetScriptBodyExpanded((prev) => ({
                      ...prev,
                      [scriptRowKey]: !isScriptBodyExpanded,
                    }));
                  };
                  const handleScriptBodyKeyDown = (e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    handleToggleScriptBody();
                  };
                  return (
                    <div
                      key={scriptRowKey}
                      style={{
                        border: '1px solid var(--pm-border, #e4e7ec)',
                        borderRadius: 8,
                        padding: 12,
                        marginBottom: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <Input
                          placeholder="Название (для себя)"
                          value={item.name}
                          onChange={(e) => handleUpdateCabinetScript(index, { name: e.target.value })}
                          style={{ flex: '1 1 260px', maxWidth: 400 }}
                          aria-label={`Скрипт ${index + 1}: название`}
                        />
                        <Space>
                          <Switch
                            checked={item.enabled !== false}
                            onChange={(checked) => handleUpdateCabinetScript(index, { enabled: checked })}
                            aria-label="Включён"
                          />
                          <span className="sett-cell--muted">Активен</span>
                        </Space>
                        <div
                          style={{
                            marginLeft: 'auto',
                            display: 'flex',
                            gap: 8,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteCabinetScript(index)}
                            aria-label="Удалить скрипт"
                          >
                            Удалить
                          </Button>
                          <Tooltip title={isScriptBodyExpanded ? 'Свернуть код и настройки' : 'Развернуть код и настройки'}>
                            <Button
                              type="default"
                              icon={isScriptBodyExpanded ? <UpOutlined /> : <DownOutlined />}
                              onClick={handleToggleScriptBody}
                              onKeyDown={handleScriptBodyKeyDown}
                              tabIndex={0}
                              aria-expanded={isScriptBodyExpanded}
                              aria-controls={`cabinet-script-body-${scriptRowKey}`}
                              aria-label={
                                isScriptBodyExpanded
                                  ? `Свернуть настройки скрипта ${index + 1}`
                                  : `Развернуть настройки скрипта ${index + 1}`
                              }
                            />
                          </Tooltip>
                        </div>
                      </div>
                      {isScriptBodyExpanded && (
                        <div
                          id={`cabinet-script-body-${scriptRowKey}`}
                          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                        >
                          <Input.TextArea
                            rows={3}
                            value={item.content}
                            onChange={(e) => handleUpdateCabinetScript(index, { content: e.target.value })}
                            placeholder='Вставьте фрагмент из Метрики/Jivo целиком или один тег <script src="//…" async></script>'
                            aria-label={`Скрипт ${index + 1}: код`}
                          />
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 240px', minWidth: 220 }}>
                              <label className="sett-cell--muted">Страницы</label>
                              <Select
                                mode="multiple"
                                value={item.pages}
                                onChange={(val) =>
                                  handleUpdateCabinetScript(index, { pages: val && val.length ? val : ['all'] })
                                }
                                style={{ width: '100%' }}
                                aria-label="Страницы для загрузки"
                              >
                                <Option value="all">Все страницы</Option>
                                <Option value="login">Авторизация</Option>
                                <Option value="dashboard">Dashboard</Option>
                                <Option value="proxies">Прокси</Option>
                                <Option value="tariffs">Тарифы</Option>
                                <Option value="proxy-checker">Проверка прокси</Option>
                                <Option value="orders">Заказы</Option>
                                <Option value="settings">Настройки</Option>
                              </Select>
                            </div>
                            <div style={{ flex: '1 1 240px', minWidth: 220 }}>
                              <label className="sett-cell--muted">Уровни привилегий</label>
                              <Select
                                mode="multiple"
                                value={item.tiers}
                                onChange={(val) =>
                                  handleUpdateCabinetScript(index, { tiers: val && val.length ? val : ['all'] })
                                }
                                style={{ width: '100%' }}
                                aria-label="Уровни привилегий для загрузки"
                              >
                                <Option value="all">Все уровни</Option>
                                <Option value="bronze">Бронза</Option>
                                <Option value="silver">Серебро</Option>
                                <Option value="gold">Золото</Option>
                              </Select>
                            </div>
                            <div style={{ flex: '1 1 320px', minWidth: 260 }}>
                              <label className="sett-cell--muted">Период действия</label>
                              <DatePicker.RangePicker
                                showTime
                                style={{ width: '100%' }}
                                value={[
                                  item.starts_at ? dayjs(item.starts_at) : null,
                                  item.ends_at ? dayjs(item.ends_at) : null,
                                ]}
                                onChange={(range) => {
                                  const starts = range && range[0] ? range[0].toISOString() : null;
                                  const ends = range && range[1] ? range[1].toISOString() : null;
                                  handleUpdateCabinetScript(index, { starts_at: starts, ends_at: ends });
                                }}
                                aria-label="Период действия скрипта"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {clientCabinetTab === 'tg-proxies' && (
            <div className="sett-card">
              <div className="sett-card__header sett-card__header--row">
                <h3 className="sett-card__title">Прокси для Telegram-бота</h3>
                <Button
                  type={lkSectionDirty.tgProxies ? 'primary' : 'default'}
                  disabled={!lkSectionDirty.tgProxies}
                  loading={lkSectionSaving.tgProxies}
                  onClick={() => handleSaveLkCabinetSection('tgProxies')}
                  aria-label="Сохранить прокси Telegram-бота"
                >
                  Сохранить
                </Button>
              </div>
              <div className="sett-card__body">
                <p className="sett-cell--muted" style={{ marginBottom: 12 }}>
                  До {MAX_TELEGRAM_BOT_PROXIES} URL (socks5/http). Бот проверяет доступ к api.telegram.org и при сбое
                  переключается на следующий. Если список пуст — используется TELEGRAM_PROXY_URL из .env.
                </p>
                <div className="sett-telegram-bot-proxies">
                  {cabinetTelegramBotProxies.map((proxyUrl, index) => {
                    const trimmedUrl = String(proxyUrl || '').trim();
                    const healthEntry = lookupTelegramBotProxyHealth(cabinetTelegramBotProxyHealth, trimmedUrl);
                    const isChecking = cabinetTelegramBotProxyHealthChecking && Boolean(trimmedUrl);
                    let statusTag = null;
                    if (trimmedUrl) {
                      if (isChecking) {
                        statusTag = (
                          <Tag
                            className="sett-telegram-bot-proxies__tag"
                            color="processing"
                            icon={<SyncOutlined spin />}
                            aria-label={`Прокси ${index + 1}: проверка`}
                          >
                            Проверка…
                          </Tag>
                        );
                      } else if (healthEntry?.ok) {
                        statusTag = (
                          <Tag
                            className="sett-telegram-bot-proxies__tag sett-telegram-bot-proxies__tag--ok"
                            color="success"
                            icon={<CheckCircleOutlined />}
                            aria-label={`Прокси ${index + 1}: активен`}
                          >
                            Активен
                          </Tag>
                        );
                      } else if (healthEntry) {
                        statusTag = (
                          <Tag
                            className="sett-telegram-bot-proxies__tag sett-telegram-bot-proxies__tag--fail"
                            color="error"
                            icon={<CloseCircleOutlined />}
                            title={healthEntry.error || undefined}
                            aria-label={`Прокси ${index + 1}: неактивен`}
                          >
                            Неактивен
                          </Tag>
                        );
                      }
                    }
                    return (
                    <div key={`tg-bot-proxy-${index}`} className="sett-telegram-bot-proxies__row">
                      <Input
                        value={proxyUrl}
                        onChange={(e) => handleTelegramBotProxyChange(index, e.target.value)}
                        placeholder="socks5://user:pass@host:port"
                        aria-label={`Прокси Telegram-бота ${index + 1}`}
                      />
                      <div className="sett-telegram-bot-proxies__status">
                        {statusTag}
                      </div>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveTelegramBotProxy(index)}
                        aria-label={`Удалить прокси ${index + 1}`}
                      />
                    </div>
                    );
                  })}
                  <Button
                    type="dashed"
                    onClick={handleAddTelegramBotProxy}
                    disabled={cabinetTelegramBotProxies.length >= MAX_TELEGRAM_BOT_PROXIES}
                    aria-label="Добавить прокси для Telegram-бота"
                  >
                    Добавить прокси
                  </Button>
                </div>
              </div>
            </div>
            )}

            {clientCabinetTab === 'forbidden-resources' && (
            <div className="sett-card">
              <div className="sett-card__header sett-card__header--row">
                <h3 className="sett-card__title">Запрещенные ресурсы (дефолт при покупке)</h3>
                <Button
                  type={lkSectionDirty.forbiddenResources ? 'primary' : 'default'}
                  disabled={!lkSectionDirty.forbiddenResources}
                  loading={lkSectionSaving.forbiddenResources}
                  onClick={() => handleSaveLkCabinetSection('forbiddenResources')}
                  aria-label="Сохранить запрещенные ресурсы"
                >
                  Сохранить
                </Button>
              </div>
              <div className="sett-card__body">
                <p className="sett-cell--muted" style={{ marginBottom: 12 }}>
                  Применяется ко всем новым прокси, купленным через личный кабинет.
                  В Kraken передаются только ID списков сайтов (не URL) и порты назначения.
                  Выключенный тумблер = запрет содержимого списка (blacklist), включённый = только разрешить (whitelist).
                </p>
                <Form.Item
                  name="default_kraken_is_allow"
                  label="Доступ прокси по списку ресурсов"
                  valuePropName="checked"
                >
                  <Switch aria-label="Режим списка ресурсов: включено — разрешить, выключено — запретить" />
                </Form.Item>
                <Form.Item
                  name="default_kraken_is_ports"
                  label="Доступ к портам из списка"
                  valuePropName="checked"
                >
                  <Switch aria-label="Режим списка портов: включено — разрешить, выключено — запретить" />
                </Form.Item>
                <Form.Item
                  name="default_kraken_allow_input"
                  label="ID списков ресурсов Kraken"
                  extra="Только числовые ID списков из Kraken, через запятую. URL и домены не принимаются."
                >
                  <Input
                    placeholder="например: 1, 2, 5"
                    aria-label="ID списков ресурсов Kraken, через запятую"
                    autoComplete="off"
                  />
                </Form.Item>
                <Form.Item
                  name="default_kraken_targetport_input"
                  label="Порты назначения"
                  extra="Номера портов через запятую (например 25, 465)."
                >
                  <Input
                    placeholder="например: 25, 465"
                    aria-label="Порты назначения, через запятую"
                    autoComplete="off"
                  />
                </Form.Item>
              </div>
            </div>
            )}
          </Form>

          {clientCabinetTab === 'main' && (
            <>
              <div className="sett-info sett-info--spaced-top">
                <svg className="sett-info__icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
                </svg>
                <div className="sett-info__body">
                  <p className="sett-info__title">Отложенная очистка в Kraken</p>
                  <p className="sett-info__desc">
                    Миграция с упавшего сервера: новая прокси создаётся на живом, старая снимается из менеджера,
                    а leftover в исходном Kraken ставится в очередь. Когда сервер снова отвечает — удаляем те,
                    что уже мигрировали. Также добираем leftover, которые есть в Kraken, но больше нет в менеджере.
                  </p>
                </div>
              </div>

              <div className="sett-card">
                <div className="sett-card__header">
                  <h3 className="sett-card__title">Расписание очистки orphan-прокси</h3>
                </div>
                <div className="sett-card__body">
                  <Form
                    form={krakenCleanupForm}
                    layout="vertical"
                    onFinish={handleSaveKrakenCleanup}
                    initialValues={{ enabled: false, interval_minutes: 30, max_attempts: 10 }}
                  >
                    <div className="sett-form-grid sett-form-grid--single">
                      <Form.Item
                        name="enabled"
                        label="Включить периодический запуск"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                      <Form.Item
                        name="interval_minutes"
                        label="Интервал проверки (мин)"
                        rules={[
                          {
                            validator: (_, value) => {
                              const n = Number(value);
                              if (!Number.isFinite(n) || n < 1 || n > 1440) {
                                return Promise.reject(new Error('Укажите число от 1 до 1440'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          min={1}
                          max={1440}
                          placeholder="30"
                          aria-label="Интервал проверки очистки orphan-прокси в минутах"
                        />
                      </Form.Item>
                      <Form.Item
                        name="max_attempts"
                        label="Макс. попыток на запись"
                        rules={[
                          {
                            validator: (_, value) => {
                              const n = Number(value);
                              if (!Number.isFinite(n) || n < 1 || n > 1000) {
                                return Promise.reject(new Error('Укажите число от 1 до 1000'));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          type="number"
                          min={1}
                          max={1000}
                          aria-label="Максимальное число попыток очистки orphan-прокси"
                        />
                      </Form.Item>
                    </div>
                    {(krakenCleanupMeta.last_run_at || krakenCleanupMeta.last_run_summary) && (
                      <div className="sett-section-divider">Последний запуск</div>
                    )}
                    {krakenCleanupMeta.last_run_at && (
                      <p className="sett-info__desc" style={{ marginBottom: 8 }}>
                        {krakenCleanupMeta.last_run_at}
                      </p>
                    )}
                    {krakenCleanupMeta.last_run_summary && typeof krakenCleanupMeta.last_run_summary === 'object' && (
                      <p className="sett-info__desc">
                        Обработано: {krakenCleanupMeta.last_run_summary.processed ?? '—'}
                        {', '}
                        удалено в Kraken: {krakenCleanupMeta.last_run_summary.done ?? '—'}
                        {', '}
                        ошибок: {krakenCleanupMeta.last_run_summary.failed ?? '—'}
                        {krakenCleanupMeta.last_run_summary.skipped_connection_down != null
                          && krakenCleanupMeta.last_run_summary.skipped_connection_down > 0
                          ? `, connection down: ${krakenCleanupMeta.last_run_summary.skipped_connection_down}`
                          : ''}
                        {krakenCleanupMeta.last_run_summary.reconciled != null
                          && krakenCleanupMeta.last_run_summary.reconciled > 0
                          ? `, leftover найдено: ${krakenCleanupMeta.last_run_summary.reconciled}`
                          : ''}
                      </p>
                    )}
                    <div className="sett-form-actions">
                      <button
                        type="submit"
                        className={`sett-form-btn sett-form-btn--primary${krakenCleanupLoading ? ' sett-form-btn--loading' : ''}`}
                        disabled={krakenCleanupLoading}
                      >
                        Сохранить
                      </button>
                      <button
                        type="button"
                        className={`sett-form-btn sett-form-btn--secondary${krakenCleanupLoading ? ' sett-form-btn--loading' : ''}`}
                        onClick={handleLoadKrakenCleanup}
                        disabled={krakenCleanupLoading}
                      >
                        Обновить
                      </button>
                      <button
                        type="button"
                        className={`sett-form-btn sett-form-btn--secondary${krakenCleanupRunLoading ? ' sett-form-btn--loading' : ''}`}
                        onClick={handleRunKrakenCleanupNow}
                        disabled={krakenCleanupRunLoading || krakenCleanupLoading}
                        tabIndex={0}
                        aria-label="Запустить очистку orphan-прокси в Kraken сейчас"
                      >
                        Запустить сейчас
                      </button>
                    </div>
                  </Form>
                </div>
              </div>
            </>
          )}
        </div>
        </ConfigProvider>
      )}

      <Modal
        className="light-modal"
        title={editingUser ? "Редактировать пользователя" : "Добавить нового пользователя"}
        open={modalVisible}
        onCancel={handleUserModalCancel}
        maskClosable={false}
        wrapProps={userModalBackdrop}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingUser ? handleUpdateUser : handleCreateUser}
          initialValues={{
            role: 'user'
          }}
        >
          <Form.Item
            name="username"
            label="Имя пользователя"
            rules={[
              { required: true, message: 'Введите имя пользователя' },
              { min: 3, message: 'Минимум 3 символа' },
              { max: 50, message: 'Максимум 50 символов' }
            ]}
          >
            <Input placeholder="Введите имя пользователя" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? "Новый пароль (оставьте пустым, чтобы не менять)" : "Пароль"}
            rules={[
              ...(editingUser ? [] : [{ required: true, message: 'Введите пароль' }]),
              { min: 6, message: 'Минимум 6 символов' }
            ]}
          >
            <Input.Password 
              placeholder={editingUser ? "Введите новый пароль или оставьте пустым" : "Введите пароль"}
              autoComplete={editingUser ? "new-password" : "new-password"}
            />
          </Form.Item>

          <Form.Item
            name="full_name"
            label="Полное имя (опционально)"
          >
            <Input placeholder="Введите полное имя" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Роль"
            rules={[{ required: true, message: 'Выберите роль' }]}
          >
            <Select placeholder="Выберите роль">
              <Option value="admin">Администратор</Option>
              <Option value="manager">Менеджер</Option>
              <Option value="user">Пользователь</Option>
              <Option value="demo">Демо</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="telegram_username"
            label="Telegram (логин)"
          >
            <Input placeholder="@username или username" aria-label="Telegram логин пользователя" />
          </Form.Item>

          <Form.Item
            name="telegram_chat_id"
            label="Telegram chat_id"
            rules={[
              {
                validator: (_, value) => {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const parsed = parseInt(String(value).trim(), 10);
                  if (!Number.isFinite(parsed)) {
                    return Promise.reject(new Error('Введите числовой chat_id'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Числовой chat_id" aria-label="Telegram chat_id пользователя" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? "Сохранить изменения" : "Создать пользователя"}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingUser(null);
                form.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
